// MPEG-4 / ISO Base Media File Format parser (.m4a, .mp4, .m4b, .mov audio).
//
// An MP4 file is a tree of "boxes" (QuickTime calls them "atoms"). Each box
// starts with a 4-byte size and a 4-character type. The audio itself sits in
// one big `mdat` box with no framing at all: the only way to find sample N is
// to read the sample tables inside `moov` (stsz, stsc, stco, stts). So this
// parser walks the box tree first, then rebuilds each sample's byte range and
// time from those tables, and finally hangs the samples under their `mdat`.
import { ascii, BitFields, channelName, field, fmtBytes, fmtHz, fmtKbps, hex, plural, u16be, u24be, u32be, u64be, utf16Str, utf8Str } from './bits';
import type { AudioInfo, Explanation, Field, FormatParser, Note, ParsedFile, Picture, Segment, SegmentKind, SegmentLink, Tag } from './types';

// ---------------------------------------------------------------------------
// Small helpers

const MAX_DEPTH = 24;
/** How many entries of a big table (stsz, stco, …) we list as fields. */
const TABLE_LIMIT = 200;
/** Seconds between 1904-01-01 (the MP4 epoch) and 1970-01-01. */
const EPOCH_1904 = 2082844800;

const i16 = (b: Uint8Array, o: number) => (u16be(b, o) << 16) >> 16;
const i32 = (b: Uint8Array, o: number) => u32be(b, o) | 0;
/** Signed 64-bit read (enough precision for edit-list media times). */
const i64 = (b: Uint8Array, o: number) => i32(b, o) * 4294967296 + u32be(b, o + 4);
const fixed16 = (b: Uint8Array, o: number) => u32be(b, o) / 65536;
const fmt3 = (v: number) => (+v.toFixed(3)).toLocaleString('en-US');
const n = (v: number) => v.toLocaleString('en-US');

/** A 4CC made safe for display: control bytes become dots. */
function fourcc(b: Uint8Array, o: number): string {
  let s = '';
  for (let i = 0; i < 4; i++) {
    const c = b[o + i] ?? 0;
    s += c < 0x20 || (c >= 0x7f && c < 0xa0) ? '.' : String.fromCharCode(c);
  }
  return s;
}

function mp4Date(secs: number): string {
  if (!secs) return 'Not set (0)';
  const d = new Date((secs - EPOCH_1904) * 1000);
  if (isNaN(d.getTime())) return String(secs);
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

/** ISO 639-2/T language packed as three 5-bit letters (offset 0x60). */
function language(code: number): string {
  if (code === 0x7fff) return 'Unspecified';
  if (code < 0x400) return `Macintosh language code ${code}`;
  return String.fromCharCode(((code >> 10) & 31) + 0x60, ((code >> 5) & 31) + 0x60, (code & 31) + 0x60);
}

/** Field list that silently drops fields outside [lo, hi) (for truncated boxes). */
class Fields {
  readonly list: Field[] = [];
  constructor(private readonly lo: number, private readonly hi: number) {}
  add(name: string, value: string | number, off: number, size: number, note?: string, raw?: number | bigint): void {
    if (size > 0 && off >= this.lo && off + size <= this.hi) this.list.push(field(name, value, off, size, note, raw));
  }
  addBits(fs: Field[]): void {
    for (const f of fs) if (f.bits > 0 && f.bit >= this.lo * 8 && f.bit + f.bits <= this.hi * 8) this.list.push(f);
  }
}

// ---------------------------------------------------------------------------
// Box catalogue

interface BoxDef {
  kind: SegmentKind;
  label: string;
  /** Full boxes carry a 1-byte version and 3 bytes of flags after the header. */
  full?: boolean;
  /** Plain containers: the body is just child boxes. */
  container?: boolean;
}

const BOXES: Record<string, BoxDef> = {
  ftyp: { kind: 'header', label: 'File type' },
  styp: { kind: 'header', label: 'Segment type' },
  moov: { kind: 'container', label: 'Movie', container: true },
  mvhd: { kind: 'header', label: 'Movie header', full: true },
  iods: { kind: 'header', label: 'Object descriptor', full: true },
  trak: { kind: 'container', label: 'Track', container: true },
  tkhd: { kind: 'header', label: 'Track header', full: true },
  tref: { kind: 'container', label: 'Track references', container: true },
  edts: { kind: 'container', label: 'Edits', container: true },
  elst: { kind: 'header', label: 'Edit list', full: true },
  mdia: { kind: 'container', label: 'Media', container: true },
  mdhd: { kind: 'header', label: 'Media header', full: true },
  hdlr: { kind: 'header', label: 'Handler', full: true },
  minf: { kind: 'container', label: 'Media information', container: true },
  smhd: { kind: 'header', label: 'Sound media header', full: true },
  vmhd: { kind: 'header', label: 'Video media header', full: true },
  nmhd: { kind: 'header', label: 'Null media header', full: true },
  dinf: { kind: 'container', label: 'Data information', container: true },
  dref: { kind: 'header', label: 'Data references', full: true },
  'url ': { kind: 'header', label: 'Data location', full: true },
  stbl: { kind: 'container', label: 'Sample table', container: true },
  stsd: { kind: 'header', label: 'Sample descriptions', full: true },
  stts: { kind: 'index', label: 'Time-to-sample', full: true },
  ctts: { kind: 'index', label: 'Composition offsets', full: true },
  stsc: { kind: 'index', label: 'Sample-to-chunk', full: true },
  stsz: { kind: 'index', label: 'Sample sizes', full: true },
  stz2: { kind: 'index', label: 'Compact sample sizes', full: true },
  stco: { kind: 'index', label: 'Chunk offsets', full: true },
  co64: { kind: 'index', label: 'Chunk offsets (64-bit)', full: true },
  stss: { kind: 'index', label: 'Sync samples', full: true },
  sdtp: { kind: 'index', label: 'Sample dependencies', full: true },
  sgpd: { kind: 'index', label: 'Sample group description', full: true },
  sbgp: { kind: 'index', label: 'Sample-to-group', full: true },
  udta: { kind: 'metadata', label: 'User data', container: true },
  meta: { kind: 'metadata', label: 'Metadata' },
  ilst: { kind: 'metadata', label: 'iTunes tag list', container: true },
  free: { kind: 'padding', label: 'Free space' },
  skip: { kind: 'padding', label: 'Free space' },
  wide: { kind: 'padding', label: 'Wide placeholder' },
  mdat: { kind: 'audio', label: 'Media data' },
  mvex: { kind: 'container', label: 'Movie extends', container: true },
  mehd: { kind: 'header', label: 'Movie extends header', full: true },
  trex: { kind: 'header', label: 'Track extends defaults', full: true },
  moof: { kind: 'container', label: 'Movie fragment', container: true },
  mfhd: { kind: 'header', label: 'Fragment header', full: true },
  traf: { kind: 'container', label: 'Track fragment', container: true },
  tfhd: { kind: 'header', label: 'Track fragment header', full: true },
  tfdt: { kind: 'header', label: 'Decode time', full: true },
  trun: { kind: 'index', label: 'Track run', full: true },
  mfra: { kind: 'index', label: 'Fragment random access', container: true },
  tfra: { kind: 'index', label: 'Track fragment random access', full: true },
  mfro: { kind: 'index', label: 'Fragment random access offset', full: true },
  sidx: { kind: 'index', label: 'Segment index', full: true },
  esds: { kind: 'header', label: 'Elementary stream descriptor', full: true },
  btrt: { kind: 'header', label: 'Bit rate' },
  alac: { kind: 'header', label: 'ALAC magic cookie' },
  dfLa: { kind: 'header', label: 'FLAC configuration', full: true },
  dOps: { kind: 'header', label: 'Opus configuration' },
  dac3: { kind: 'header', label: 'AC-3 configuration' },
  dec3: { kind: 'header', label: 'E-AC-3 configuration' },
  wave: { kind: 'header', label: 'QuickTime codec extras', container: true },
  frma: { kind: 'header', label: 'Original format' },
  chan: { kind: 'header', label: 'Channel layout', full: true },
};

const HANDLERS: Record<string, string> = {
  soun: 'Sound', vide: 'Video', hint: 'Hint', meta: 'Timed metadata', text: 'Text', sbtl: 'Subtitles',
  subt: 'Subtitles', mdir: 'iTunes metadata', 'url ': 'Data (file reference)', alis: 'Data (alias)', tmcd: 'Timecode',
};

const BRANDS: Record<string, string> = {
  'M4A ': 'Apple iTunes audio (.m4a)',
  'M4B ': 'Apple audiobook (.m4b); players remember the play position',
  'M4P ': 'Apple protected (DRM) audio',
  'M4V ': 'Apple iTunes video',
  isom: 'ISO Base Media File Format, the generic MP4 base',
  iso2: 'ISO Base Media, version 2 features',
  iso3: 'ISO Base Media, version 3 features',
  iso4: 'ISO Base Media, version 4 features',
  iso5: 'ISO Base Media, version 5 features (used for fragments)',
  iso6: 'ISO Base Media, version 6 features',
  iso8: 'ISO Base Media, version 8 features',
  mp41: 'MPEG-4 file format, version 1',
  mp42: 'MPEG-4 file format, version 2',
  mp71: 'MPEG-7 metadata',
  dash: 'MPEG-DASH streaming segments',
  msdh: 'MPEG-DASH media segment',
  cmfc: 'Common Media Application Format (CMAF)',
  avc1: 'Uses H.264 video features',
  'qt  ': 'Apple QuickTime movie (.mov)',
  '3gp4': '3GPP mobile (release 4)',
  '3gp5': '3GPP mobile (release 5)',
  '3gp6': '3GPP mobile (release 6)',
  '3g2a': '3GPP2 mobile',
  dby1: 'Dolby audio (AC-3 / E-AC-3)',
  mp4a: 'MPEG-4 audio',
  F4A: 'Adobe Flash audio',
};

// ---------------------------------------------------------------------------
// AAC AudioSpecificConfig

const AOT_NAMES: Record<number, string> = {
  0: 'Null', 1: 'AAC Main', 2: 'AAC LC (Low Complexity)', 3: 'AAC SSR', 4: 'AAC LTP', 5: 'SBR (Spectral Band Replication)',
  6: 'AAC Scalable', 7: 'TwinVQ', 8: 'CELP', 9: 'HVXC', 12: 'TTSI', 13: 'Main synthetic', 14: 'Wavetable synthesis',
  15: 'General MIDI', 16: 'Algorithmic synthesis', 17: 'ER AAC LC', 19: 'ER AAC LTP', 20: 'ER AAC Scalable',
  21: 'ER TwinVQ', 22: 'ER BSAC', 23: 'ER AAC LD', 24: 'ER CELP', 25: 'ER HVXC', 26: 'ER HILN', 27: 'ER Parametric',
  28: 'SSC', 29: 'PS (Parametric Stereo)', 30: 'MPEG Surround', 32: 'MPEG Layer-1', 33: 'MPEG Layer-2',
  34: 'MPEG Layer-3', 35: 'DST', 36: 'ALS', 37: 'SLS', 38: 'SLS non-core', 39: 'ER AAC ELD', 40: 'SMR Simple',
  41: 'SMR Main', 42: 'USAC (xHE-AAC)',
};
const aotName = (v: number) => `${AOT_NAMES[v] ?? 'Reserved'} (${v})`;
const AAC_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
const AAC_CHANNELS: Record<number, [number, string]> = {
  0: [0, 'Set inside the stream (program config element)'], 1: [1, 'Mono (C)'], 2: [2, 'Stereo (L, R)'], 3: [3, '3.0 (C, L, R)'],
  4: [4, '4.0 (C, L, R, rear C)'], 5: [5, '5.0'], 6: [6, '5.1'], 7: [8, '7.1'], 11: [7, '6.1'], 12: [8, '7.1 (rear)'],
  13: [24, '22.2'], 14: [8, '7.1 (top)'],
};

interface AscInfo {
  aot: number;
  sampleRate: number;
  channelConfig: number;
  sbr: boolean;
  ps: boolean;
  extSampleRate?: number;
  frameLength: number;
  fields: Field[];
}

/** Parses an AudioSpecificConfig (ISO 14496-3 §1.6.2.1) as bit fields. */
function parseAsc(b: Uint8Array, start: number, end: number): AscInfo {
  const bf = new BitFields(b, start);
  const endBit = end * 8;
  const left = () => endBit - bf.pos;
  const readAot = (label: string, note?: string): number => {
    const v = bf.read(label, 5, (x) => (x === 31 ? '31 (escape: a 6-bit value follows)' : aotName(x)), note);
    return v === 31 ? 32 + bf.read(`${label} (extended)`, 6, (x) => aotName(32 + x), 'Types above 30 use 6 more bits: type = 32 + value.') : v;
  };
  const readRate = (label: string, note?: string): number => {
    const idx = bf.read(label, 4, (x) => (x === 15 ? '15 (explicit: a 24-bit rate follows)' : AAC_RATES[x] ? `${fmtHz(AAC_RATES[x])} (index ${x})` : `Reserved (${x})`), note);
    return idx === 15 ? bf.read('Sampling frequency', 24, (x) => fmtHz(x), 'A rate that is not in the standard table, written out in full.') : AAC_RATES[idx] ?? 0;
  };
  let aot = readAot('Audio object type', 'The AAC profile. 2 = AAC-LC, 5 = HE-AAC (SBR), 29 = HE-AAC v2 (SBR + PS).');
  const rate = readRate('Sampling frequency index', 'An index into the standard rate table. For HE-AAC this is the core (half) rate.');
  const ch = bf.read('Channel configuration', 4, (x) => `${AAC_CHANNELS[x]?.[1] ?? 'Reserved'} (${x})`, 'Speaker layout code; 1 = mono, 2 = stereo.');
  let sbr = false, ps = false, extRate: number | undefined, extAot = 0;
  // Hierarchical (explicit) signalling: the first type is SBR or PS and the core type follows.
  if (aot === 5 || aot === 29) {
    extAot = 5;
    sbr = true;
    ps = aot === 29;
    extRate = readRate('Extension sampling frequency index', 'The output rate after SBR doubles the core rate.');
    aot = readAot('Core audio object type', 'The core codec under SBR, almost always AAC-LC (2).');
  }
  let frameLength = 1024;
  if ([1, 2, 3, 4, 6, 7, 17, 19, 20, 21, 22, 23].includes(aot) && left() >= 3) {
    // GASpecificConfig
    if (bf.read('Frame length flag', 1, (x) => (x ? '960 samples' : '1,024 samples'), 'Samples per frame; 1,024 is normal, 960 is used in digital radio.')) frameLength = 960;
    if (bf.read('Depends on core coder', 1, (x) => (x ? 'Yes' : 'No'), 'Used only by scalable AAC.')) bf.read('Core coder delay', 14);
    const extFlag = bf.read('Extension flag', 1, (x) => (x ? 'Yes' : 'No'), 'Set for error-resilient object types.');
    if (ch === 0) return { aot, sampleRate: rate, channelConfig: ch, sbr, ps, extSampleRate: extRate, frameLength, fields: bf.fields };
    if (aot === 6 || aot === 20) bf.read('Layer number', 3);
    if (extFlag) {
      if (aot === 22) { bf.read('Number of subframes', 5); bf.read('Layer length', 11); }
      if ([17, 19, 20, 23].includes(aot)) {
        bf.read('Section data resilience', 1); bf.read('Scalefactor data resilience', 1); bf.read('Spectral data resilience', 1);
      }
      bf.read('Extension flag 3', 1);
    }
  }
  if (aot >= 17 && aot <= 27 && aot !== 18 && left() >= 2) bf.read('Error protection config', 2);
  // Backward-compatible signalling: a sync word 0x2B7 after the core config says
  // "SBR is (or is not) present". Old decoders stop reading before it.
  if (extAot !== 5 && left() >= 16 && bf.peek(11) === 0x2b7) {
    bf.read('Sync extension type', 11, (x) => hex(x, 3), '0x2B7 marks backward-compatible SBR signalling.');
    const e = readAot('Extension audio object type', '5 means the next bits say whether SBR is used.');
    if (e === 5 && left() >= 1) {
      sbr = bf.read('SBR present flag', 1, (x) => (x ? 'Yes (HE-AAC)' : 'No (plain AAC-LC)'), 'Spectral Band Replication rebuilds the high frequencies at twice the core rate.') === 1;
      if (sbr) {
        extRate = readRate('Extension sampling frequency index', 'The output rate after SBR.');
        if (left() >= 12 && bf.peek(11) === 0x548) {
          bf.read('Sync extension type', 11, (x) => hex(x, 3), '0x548 marks Parametric Stereo signalling.');
          ps = bf.read('PS present flag', 1, (x) => (x ? 'Yes (HE-AAC v2)' : 'No'), 'Parametric Stereo rebuilds stereo from a mono core plus a few hints.') === 1;
        }
      }
    }
  }
  return { aot, sampleRate: rate, channelConfig: ch, sbr, ps, extSampleRate: extRate, frameLength, fields: bf.fields };
}

// ---------------------------------------------------------------------------
// Parse state

interface SampleTable {
  count: number;
  off: Float64Array;
  size: Uint32Array;
  /** Decode time of each sample, in media timescale ticks. */
  t0: Float64Array;
  dur: Float64Array;
  /** Fragmented files: where each sample's trun entry is, and its length (0 = none). */
  entryOff?: Float64Array;
  entryLen?: Uint8Array;
}

interface EntryInfo {
  format: string;
  codec: string;
  codecLong: string;
  sampleRate?: number;
  channels?: number;
  bitsPerSample?: number;
  lossless: boolean;
  asc?: AscInfo;
  oti?: number;
  avgBitrate?: number;
  maxBitrate?: number;
  preSkip?: number;
  channelMode?: string;
}

interface Track {
  id: number;
  handler: string;
  handlerName: string;
  timescale: number;
  mediaDuration: number;
  language: string;
  entries: EntryInfo[];
  /** Edit list entries: duration in movie timescale, media time in media timescale (-1 = empty edit). */
  edits: { segDur: number; mediaTime: number }[];
  stts?: [number, number];
  stsc?: [number, number];
  stsz?: { off: number; count: number; constSize: number; fieldBits: number };
  stco?: { off: number; count: number; wide: boolean };
  // Fragment runs (fMP4) append here instead.
  frag: { off: number[]; size: number[]; t0: number[]; dur: number[]; entry: number[]; entryLen: number[] };
  fragTime: number;
  samples?: SampleTable;
}

interface FragState {
  trackId: number;
  base: number;
  defDur: number;
  defSize: number;
  /** Where the next trun's data starts when it has no data_offset. */
  next: number;
  decodeTime?: number;
}

class Ctx {
  notes: Note[] = [];
  tags: Tag[] = [];
  pictures: Picture[] = [];
  tracks: Track[] = [];
  track?: Track;
  mdats: Segment[] = [];
  movieTimescale = 0;
  movieDuration = 0;
  brand?: string;
  encoder?: string;
  smpb?: { delay: number; padding: number; length: number };
  moovStart = -1;
  moofCount = 0;
  moofStart = 0;
  trex = new Map<number, { dur: number; size: number }>();
  frag?: FragState;
  constructor(readonly b: Uint8Array) {}
  trackById(id: number): Track | undefined {
    return this.tracks.find((t) => t.id === id);
  }
}

function newTrack(): Track {
  return { id: 0, handler: '', handlerName: '', timescale: 0, mediaDuration: 0, language: '', entries: [], edits: [], frag: { off: [], size: [], t0: [], dur: [], entry: [], entryLen: [] }, fragTime: 0 };
}

// ---------------------------------------------------------------------------
// Box walking

interface BoxHeader {
  start: number;
  end: number;
  type: string;
  /** Header length: 8, or 16 with a 64-bit size, plus 16 for 'uuid'. */
  hs: number;
  size: number;
  sizeField: number;
  warning?: string;
}

function readHeader(b: Uint8Array, p: number, limit: number): BoxHeader | null {
  if (p + 8 > limit) return null;
  const sizeField = u32be(b, p);
  const type = fourcc(b, p + 4);
  let size = sizeField, hs = 8;
  if (sizeField === 1) {
    if (p + 16 > limit) return null;
    size = u64be(b, p + 8);
    hs = 16;
  } else if (sizeField === 0) {
    size = limit - p; // "extends to the end of the file" (or of the parent)
  }
  if (size < hs) return null;
  if (type === 'uuid') hs += 16;
  let end = p + size, warning: string | undefined;
  if (end > limit) {
    warning = `This box says it is ${n(size)} bytes long, but only ${n(limit - p)} bytes are left. The file may be cut short or damaged.`;
    end = limit;
  }
  if (p + hs > end) return null;
  return { start: p, end, type, hs, size, sizeField, warning };
}

function parseBoxes(ctx: Ctx, start: number, end: number, parent: string, depth: number): Segment[] {
  const out: Segment[] = [];
  const b = ctx.b;
  let p = start;
  while (p < end) {
    const h = depth < MAX_DEPTH ? readHeader(b, p, end) : null;
    if (!h) {
      const zeros = end - p <= 8 && b.subarray(p, end).every((x) => x === 0);
      out.push(zeros
        ? { kind: 'padding', type: 'mp4.terminator', name: 'Zero terminator', start: p, end, summary: plural(end - p, 'byte') }
        : { kind: 'unknown', type: 'mp4.invalid', name: 'Unreadable bytes', start: p, end, summary: fmtBytes(end - p),
          warning: 'These bytes do not form a valid box header, so the parser stopped reading here.' });
      break;
    }
    let seg: Segment;
    try {
      seg = parseBox(ctx, h, parent, depth);
    } catch (e) {
      seg = { kind: 'unknown', type: 'mp4.box.unknown', name: h.type, start: h.start, end: h.end, warning: `The parser could not read this box (${(e as Error).message}).` };
    }
    out.push(seg);
    p = h.end;
  }
  return out;
}

/** Builds the box-header child segment and its fields. */
function headerSeg(ctx: Ctx, h: BoxHeader, full: boolean, flagsNote?: (flags: number) => string): { seg: Segment; fields: Field[]; body: number; version: number; flags: number } {
  const b = ctx.b;
  const f = new Fields(h.start, h.end);
  const sizeText = h.sizeField === 1 ? '1 (a 64-bit size follows)' : h.sizeField === 0 ? `0 (runs to the end: ${n(h.size)} bytes)` : `${n(h.sizeField)} bytes`;
  f.add('Size', sizeText, h.start, 4, 'Length of the whole box, header included.', h.sizeField);
  f.add('Type', h.type, h.start + 4, 4, 'Four characters that say what the box holds.');
  if (h.sizeField === 1) f.add('Large size', `${n(h.size)} bytes`, h.start + 8, 8, 'The real size, for boxes over 4 GB.', h.size);
  if (h.type === 'uuid') f.add('Extended type', Array.from(b.subarray(h.hs - 16 + h.start, h.start + h.hs), (x) => x.toString(16).padStart(2, '0')).join(''), h.start + h.hs - 16, 16, 'A 16-byte UUID that names a vendor-specific box.');
  let body = h.start + h.hs, version = 0, flags = 0;
  if (full && body + 4 <= h.end) {
    version = b[body];
    flags = u24be(b, body + 1);
    f.add('Version', version, body, 1, 'Version 1 usually means 64-bit times and sizes.');
    f.add('Flags', flagsNote ? `${hex(flags, 6)} (${flagsNote(flags)})` : hex(flags, 6), body + 1, 3, 'Bit flags; their meaning depends on the box type.', flags);
    body += 4;
  }
  const seg: Segment = { kind: 'container', type: 'mp4.boxheader', name: 'Box header', start: h.start, end: Math.min(body, h.end), fields: f.list, summary: `${h.type} · ${n(h.size)} bytes` };
  return { seg, fields: f.list, body: Math.min(body, h.end), version, flags };
}

interface BoxResult {
  kind?: SegmentKind;
  type?: string;
  label?: string;
  fields?: Field[] | (() => Field[]);
  children?: Segment[];
  summary?: string;
  warning?: string;
  image?: Segment['image'];
}

function parseBox(ctx: Ctx, h: BoxHeader, parent: string, depth: number): Segment {
  const b = ctx.b;
  // Context decides how some 4CCs are read.
  if (parent === 'stsd') return sampleEntry(ctx, h, depth);
  if (parent === 'ilst') return ilstItem(ctx, h, depth);
  if (parent === 'udta' && b[h.start + 4] === 0xa9 && fourcc(b, h.start + 12) !== 'data') return qtTextAtom(ctx, h);

  // QuickTime ends some atom lists with an 8-byte box of type 0.
  if (h.type === '....' && u32be(b, h.start + 4) === 0) {
    return { kind: 'padding', type: 'mp4.terminator', name: 'Terminator', start: h.start, end: h.end, summary: plural(h.end - h.start, 'byte'), children: [headerSeg(ctx, h, false).seg] };
  }
  if (parent === 'wave' && AUDIO_FORMATS[h.type]) {
    const hs = headerSeg(ctx, h, false);
    return { kind: 'header', type: 'mp4.wave.format', name: `${h.type} (Format marker)`, start: h.start, end: h.end, summary: h.type, children: [hs.seg], fields: hs.fields };
  }
  const def = BOXES[h.type];
  let isFull = !!def?.full;
  // QuickTime writes 'meta' without version/flags; ISO writes it as a full box.
  if (h.type === 'meta') isFull = fourcc(b, h.start + h.hs + 4) !== 'hdlr';
  const flagText = h.type === 'tkhd' ? tkhdFlags : h.type === 'tfhd' ? tfhdFlags : h.type === 'trun' ? trunFlags : undefined;
  const hdr = headerSeg(ctx, h, isFull, flagText);
  const children: Segment[] = [hdr.seg];
  let r: BoxResult = {};
  const body = hdr.body;

  if (def?.container) {
    const trak = h.type === 'trak' ? newTrack() : undefined;
    if (trak) ctx.tracks.push((ctx.track = trak));
    if (h.type === 'moof') { ctx.moofCount++; ctx.moofStart = h.start; }
    if (h.type === 'traf') ctx.frag = undefined;
    if (h.type === 'moov') ctx.moovStart = h.start;
    children.push(...parseBoxes(ctx, body, h.end, h.type, depth + 1));
    r.summary = containerSummary(children.length - 1);
    if (trak) {
      const t = trak;
      ctx.track = undefined;
      r.summary = `Track ${t.id} · ${HANDLERS[t.handler] ?? t.handler}${t.entries[0] ? ' · ' + t.entries[0].codec : ''}`;
    }
  } else if (h.type === 'meta') {
    children.push(...parseBoxes(ctx, body, h.end, 'meta', depth + 1));
    r.summary = isFull ? 'ISO style (full box)' : 'QuickTime style (no version/flags)';
  } else if (h.type === 'mdat') {
    r.summary = fmtBytes(h.end - body);
  } else if (def) {
    r = leafBox(ctx, h, parent, body, hdr.version, hdr.flags, depth, children) ?? {};
  } else {
    r = { kind: 'unknown', type: 'mp4.box.unknown', summary: `Unknown box “${h.type}” · ${fmtBytes(h.end - h.start)}` };
  }

  const kind = r.kind ?? def?.kind ?? 'unknown';
  const label = r.label ?? def?.label;
  const seg: Segment = {
    kind,
    type: r.type ?? (def ? `mp4.box.${h.type}` : 'mp4.box.unknown'),
    name: label ? `${h.type} (${label})` : h.type,
    start: h.start,
    end: h.end,
    summary: r.summary,
    children,
  };
  const own = r.fields;
  seg.fields = typeof own === 'function' ? () => [...hdr.fields, ...own()] : [...hdr.fields, ...(own ?? [])];
  if (r.image) seg.image = r.image;
  const warning = [h.warning, r.warning].filter(Boolean).join(' ');
  if (warning) seg.warning = warning;
  if (h.type === 'mdat') ctx.mdats.push(seg);
  return seg;
}

const containerSummary = (count: number) => (count === 0 ? 'Empty' : count === 1 ? '1 child box' : `${count} child boxes`);

const tkhdFlags = (f: number) => [f & 1 && 'enabled', f & 2 && 'in movie', f & 4 && 'in preview'].filter(Boolean).join(', ') || 'none';
const tfhdFlags = (f: number) =>
  [f & 1 && 'base offset', f & 2 && 'description index', f & 8 && 'default duration', f & 0x10 && 'default size', f & 0x20 && 'default flags', f & 0x10000 && 'duration is empty', f & 0x20000 && 'base is moof']
    .filter(Boolean).join(', ') || 'none';
const trunFlags = (f: number) =>
  [f & 1 && 'data offset', f & 4 && 'first sample flags', f & 0x100 && 'durations', f & 0x200 && 'sizes', f & 0x400 && 'sample flags', f & 0x800 && 'composition offsets']
    .filter(Boolean).join(', ') || 'none';

/** Lazily lists up to TABLE_LIMIT table entries as fields. */
function tableFields(head: Field[], start: number, count: number, entrySize: number, limit: number,
  row: (i: number, p: number) => [string, string], note: string): () => Field[] {
  return () => {
    const out = head.slice();
    const shown = Math.min(count, TABLE_LIMIT);
    let i = 0;
    for (; i < shown; i++) {
      const p = start + i * entrySize;
      if (p + entrySize > limit) break;
      const [name, value] = row(i, p);
      out.push(field(name, value, p, entrySize, i === 0 ? note : undefined));
    }
    const p = start + i * entrySize;
    const e = Math.min(limit, start + count * entrySize);
    if (i < count && e > p) out.push(field('More entries', `${n(count - i)} more (not listed)`, p, e - p, 'The rest of the table follows the same pattern.'));
    return out;
  };
}

/** Clamps a table's declared entry count to what fits in the box. */
const fitCount = (count: number, start: number, end: number, entrySize: number) => Math.max(0, Math.min(count, Math.floor((end - start) / entrySize)));

function leafBox(ctx: Ctx, h: BoxHeader, parent: string, p: number, version: number, flags: number, depth: number, children: Segment[]): BoxResult | undefined {
  const b = ctx.b;
  const end = h.end;
  const f = new Fields(h.start, end);
  const t = ctx.track;
  switch (h.type) {
    case 'ftyp':
    case 'styp': {
      const major = fourcc(b, p);
      ctx.brand ??= major;
      f.add('Major brand', `${major} — ${BRANDS[major] ?? 'Unknown brand'}`, p, 4, 'The main specification this file follows.');
      f.add('Minor version', u32be(b, p + 4), p + 4, 4, 'Version of the major brand. Most readers ignore it.');
      const brands: string[] = [];
      for (let q = p + 8; q + 4 <= end; q += 4) {
        const br = fourcc(b, q);
        brands.push(br.trim());
        f.add('Compatible brand', `${br} — ${BRANDS[br] ?? 'Unknown brand'}`, q, 4, brands.length === 1 ? 'Other specifications a reader may use to play this file.' : undefined);
      }
      return { fields: f.list, summary: `${major.trim()}${brands.length ? ' · compatible: ' + brands.join(', ') : ''}` };
    }
    case 'mvhd': {
      const v1 = version === 1;
      const w = v1 ? 8 : 4;
      const rd = (o: number) => (v1 ? u64be(b, o) : u32be(b, o));
      const created = rd(p), modified = rd(p + w), ts = u32be(b, p + 2 * w), dur = rd(p + 2 * w + 4);
      ctx.movieTimescale = ts;
      ctx.movieDuration = dur;
      f.add('Creation time', mp4Date(created), p, w, 'Seconds since 1 January 1904, the classic Mac epoch.', created);
      f.add('Modification time', mp4Date(modified), p + w, w, 'When the file was last changed, same epoch.', modified);
      f.add('Timescale', `${n(ts)} ticks per second`, p + 2 * w, 4, 'The movie clock. Durations in this box and in edit lists count these ticks.', ts);
      f.add('Duration', `${n(dur)} ticks = ${ts ? fmt3(dur / ts) : '?'} s`, p + 2 * w + 4, w, 'Length of the whole presentation (the longest track).', dur);
      let q = p + 3 * w + 4;
      f.add('Preferred rate', `${fixed16(b, q)}×`, q, 4, 'Playback speed; 1.0 is normal.');
      f.add('Preferred volume', `${u16be(b, q + 4) / 256}`, q + 4, 2, 'Playback volume; 1.0 is full.');
      q += 6 + 10 + 36 + 24;
      f.add('Next track ID', u32be(b, q), q, 4, 'The ID to use if a tool adds another track.');
      return { fields: f.list, summary: `${ts ? fmt3(dur / ts) + ' s' : '?'} · timescale ${n(ts)}` };
    }
    case 'tkhd': {
      const v1 = version === 1;
      const w = v1 ? 8 : 4;
      const rd = (o: number) => (v1 ? u64be(b, o) : u32be(b, o));
      const id = u32be(b, p + 2 * w);
      if (t) t.id = id;
      const dur = rd(p + 2 * w + 8);
      f.add('Creation time', mp4Date(rd(p)), p, w, 'Seconds since 1 January 1904.');
      f.add('Modification time', mp4Date(rd(p + w)), p + w, w);
      f.add('Track ID', id, p + 2 * w, 4, 'A unique number for this track inside the file.');
      f.add('Duration', `${n(dur)} ticks${ctx.movieTimescale ? ` = ${fmt3(dur / ctx.movieTimescale)} s` : ''}`, p + 2 * w + 8, w, 'Track length in the movie timescale (from mvhd).', dur);
      let q = p + 3 * w + 8 + 8;
      f.add('Layer', i16(b, q), q, 2, 'Stacking order for video; unused for audio.');
      f.add('Alternate group', i16(b, q + 2), q + 2, 2, 'Tracks in the same group are alternatives (for example, two languages).');
      f.add('Volume', `${u16be(b, q + 4) / 256}`, q + 4, 2, '1.0 for audio tracks, 0 for video.');
      q += 8 + 36;
      const wd = fixed16(b, q), ht = fixed16(b, q + 4);
      f.add('Width', `${wd}`, q, 4, 'Display width; 0 for audio.');
      f.add('Height', `${ht}`, q + 4, 4, 'Display height; 0 for audio.');
      return { fields: f.list, summary: `Track ${id} · ${tkhdFlags(flags)}` };
    }
    case 'mdhd': {
      const v1 = version === 1;
      const w = v1 ? 8 : 4;
      const rd = (o: number) => (v1 ? u64be(b, o) : u32be(b, o));
      const ts = u32be(b, p + 2 * w), dur = rd(p + 2 * w + 4);
      const langCode = u16be(b, p + 3 * w + 4) & 0x7fff;
      const lang = language(langCode);
      if (t) { t.timescale = ts; t.mediaDuration = dur; t.language = lang; }
      f.add('Creation time', mp4Date(rd(p)), p, w, 'Seconds since 1 January 1904.');
      f.add('Modification time', mp4Date(rd(p + w)), p + w, w);
      f.add('Timescale', `${n(ts)} ticks per second`, p + 2 * w, 4, 'The media clock. For audio it is usually the sample rate, so one tick = one sample.', ts);
      f.add('Duration', `${n(dur)} ticks = ${ts ? fmt3(dur / ts) : '?'} s`, p + 2 * w + 4, w, 'Length of all samples, priming included.', dur);
      f.add('Language', lang, p + 3 * w + 4, 2, 'ISO 639-2 code packed as three 5-bit letters. "und" means undetermined.', langCode);
      return { fields: f.list, summary: `${fmtHz(ts)} timescale · ${ts ? fmt3(dur / ts) : '?'} s · ${lang}` };
    }
    case 'hdlr': {
      const pre = fourcc(b, p);
      const type = fourcc(b, p + 4);
      const own = parent === 'mdia' && t;
      f.add('Pre-defined', u32be(b, p) ? pre : '0', p, 4, 'Zero in MP4. QuickTime writes "mhlr" or "dhlr" here.');
      f.add('Handler type', `${type} (${HANDLERS[type] ?? 'Unknown'})`, p + 4, 4, 'What kind of data the track or metadata holds; "soun" means audio.');
      f.add('Reserved', '0', p + 8, 12, 'Three unused 32-bit values (QuickTime puts a vendor code here).');
      const q = p + 20;
      let name = '';
      if (q < end) {
        // ISO writes a null-terminated string; QuickTime writes a Pascal string (length byte first).
        const len = b[q];
        name = len === end - q - 1 && len > 0 ? utf8Str(b, q + 1, len) : utf8Str(b, q, end - q).replace(/\0.*$/s, '');
        f.add('Name', name || '(empty)', q, end - q, 'A human-readable name for the handler.');
      }
      if (own) { t.handler = type; t.handlerName = name; }
      return { fields: f.list, summary: `${type} (${HANDLERS[type] ?? 'Unknown'})${name ? ' · ' + name : ''}` };
    }
    case 'smhd':
      f.add('Balance', `${i16(b, p) / 256}`, p, 2, 'Stereo balance; 0 is centre.');
      return { fields: f.list, summary: 'Audio track' };
    case 'vmhd':
      f.add('Graphics mode', u16be(b, p), p, 2, 'How video is composed; 0 means copy.');
      return { fields: f.list, summary: 'Video track' };
    case 'dref': {
      const count = u32be(b, p);
      f.add('Entry count', count, p, 4, 'How many data locations follow.');
      children.push(...parseBoxes(ctx, p + 4, end, 'dref', depth + 1));
      return { fields: f.list, summary: plural(count, 'entry') };
    }
    case 'url ':
      return { fields: f.list, summary: flags & 1 ? 'Media data is in this file' : utf8Str(b, p, end - p).replace(/\0.*$/s, '') };
    case 'stsd': {
      const count = u32be(b, p);
      f.add('Entry count', count, p, 4, 'How many sample descriptions follow. Almost always 1.');
      children.push(...parseBoxes(ctx, p + 4, end, 'stsd', depth + 1));
      const e = t?.entries[0];
      return { fields: f.list, summary: e ? [e.codec, e.sampleRate && fmtHz(e.sampleRate), e.channels && channelName(e.channels)].filter(Boolean).join(' · ') : plural(count, 'entry') };
    }
    case 'stts': {
      const count = fitCount(u32be(b, p), p + 4, end, 8);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of (sample count, duration) runs.');
      if (t) t.stts = [p + 4, count];
      let total = 0;
      for (let i = 0; i < count; i++) total += u32be(b, p + 4 + i * 8);
      const ts = t?.timescale || 0;
      return {
        fields: tableFields(f.list, p + 4, count, 8, end, (i, q) => {
          const c = u32be(b, q), d = u32be(b, q + 4);
          return [`Run ${i + 1}`, `${n(c)} × ${n(d)} ticks${ts ? ` (${fmt3((d / ts) * 1000)} ms)` : ''}`];
        }, 'A run of samples that all last the same number of ticks.'),
        summary: `${plural(count, 'run')} · ${plural(total, 'sample')}`,
      };
    }
    case 'ctts': {
      const count = fitCount(u32be(b, p), p + 4, end, 8);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of (sample count, offset) runs.');
      return {
        fields: tableFields(f.list, p + 4, count, 8, end, (i, q) => [`Run ${i + 1}`, `${n(u32be(b, q))} × offset ${n(version ? i32(b, q + 4) : u32be(b, q + 4))}`], 'Shift between decode time and display time.'),
        summary: plural(count, 'run'),
      };
    }
    case 'stsc': {
      const count = fitCount(u32be(b, p), p + 4, end, 12);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of chunk runs.');
      if (t) t.stsc = [p + 4, count];
      return {
        fields: tableFields(f.list, p + 4, count, 12, end, (i, q) => {
          const first = u32be(b, q), per = u32be(b, q + 4);
          const next = i + 1 < count ? u32be(b, q + 12) : 0;
          return [`Run ${i + 1}`, `Chunks ${n(first)}${next ? '–' + n(next - 1) : ' onward'}: ${plural(per, 'sample')} each (description ${u32be(b, q + 8)})`];
        }, 'From this chunk on, every chunk holds this many samples.'),
        summary: plural(count, 'run'),
      };
    }
    case 'stsz':
    case 'stz2': {
      let constSize = 0, fieldBits = 32, count: number, q: number;
      if (h.type === 'stsz') {
        constSize = u32be(b, p);
        count = u32be(b, p + 4);
        f.add('Sample size', constSize ? `${n(constSize)} bytes (all samples)` : '0 (sizes listed below)', p, 4, 'One size for every sample, or 0 when each sample has its own size.', constSize);
        f.add('Sample count', count, p + 4, 4, 'Total number of samples in the track.');
        q = p + 8;
      } else {
        fieldBits = b[p + 3];
        count = u32be(b, p + 4);
        f.add('Field size', `${fieldBits} bits`, p + 3, 1, 'Bits per entry: 4, 8 or 16. Smaller entries save space.', fieldBits);
        f.add('Sample count', count, p + 4, 4, 'Total number of samples in the track.');
        q = p + 8;
        if (![4, 8, 16].includes(fieldBits)) return { fields: f.list, warning: `Invalid field size ${fieldBits}.` };
      }
      let warning: string | undefined;
      if (!constSize) {
        const fit = Math.floor(((end - q) * 8) / fieldBits);
        if (fit < count) { warning = `The table claims ${n(count)} samples, but the box only has room for ${n(fit)}.`; count = fit; }
      }
      if (t) t.stsz = { off: q, count, constSize, fieldBits };
      if (constSize) return { fields: f.list, summary: `${plural(count, 'sample')} · ${n(constSize)} bytes each`, warning };
      if (fieldBits === 4) {
        // Two entries per byte; list them as bit fields.
        const shown = Math.min(count, TABLE_LIMIT);
        const head = f.list;
        return {
          fields: () => [...head, ...Array.from({ length: shown }, (_, i): Field => ({ name: `Sample ${i + 1}`, value: `${(b[q + (i >> 1)] >> (i & 1 ? 0 : 4)) & 15} bytes`, bit: q * 8 + i * 4, bits: 4 }))],
          summary: plural(count, 'sample'), warning,
        };
      }
      const bytes = fieldBits / 8;
      const rd = bytes === 4 ? u32be : bytes === 2 ? u16be : (bb: Uint8Array, o: number) => bb[o];
      return {
        fields: tableFields(f.list, q, count, bytes, end, (i, o) => [`Sample ${i + 1}`, `${n(rd(b, o))} bytes`], 'Size of one sample in bytes.'),
        summary: plural(count, 'sample'), warning,
      };
    }
    case 'stco':
    case 'co64': {
      const wide = h.type === 'co64';
      const es = wide ? 8 : 4;
      const count = fitCount(u32be(b, p), p + 4, end, es);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of chunks.');
      if (t) t.stco = { off: p + 4, count, wide };
      return {
        fields: tableFields(f.list, p + 4, count, es, end, (i, o) => [`Chunk ${i + 1}`, `offset ${n(wide ? u64be(b, o) : u32be(b, o))}`], 'Absolute file position where this chunk of samples starts.'),
        summary: plural(count, 'chunk'),
      };
    }
    case 'stss': {
      const count = fitCount(u32be(b, p), p + 4, end, 4);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of sync (key) samples.');
      return { fields: tableFields(f.list, p + 4, count, 4, end, (i, o) => [`Sync ${i + 1}`, `Sample ${n(u32be(b, o))}`], 'A sample that can be decoded without earlier samples.'), summary: plural(count, 'sync sample') };
    }
    case 'sdtp':
      return { summary: plural(end - p, 'entry') };
    case 'sgpd': {
      const gt = fourcc(b, p);
      f.add('Grouping type', gt, p, 4, gt === 'roll' ? '"roll" groups say how many earlier samples a decoder needs first.' : 'The kind of sample group.');
      let q = p + 4, defLen = 0;
      if (version === 1) { defLen = u32be(b, q); f.add('Default length', defLen, q, 4, 'Size of each group entry.'); q += 4; }
      if (version >= 2) { f.add('Default description index', u32be(b, q), q, 4); q += 4; }
      const count = u32be(b, q);
      f.add('Entry count', count, q, 4, 'How many group descriptions follow.');
      q += 4;
      let summary = `${gt} · ${plural(count, 'entry')}`;
      for (let i = 0; i < Math.min(count, 16) && q < end; i++) {
        let len = defLen;
        if (version === 1 && defLen === 0) { len = u32be(b, q); f.add('Description length', len, q, 4); q += 4; }
        if ((gt === 'roll' || gt === 'prol') && q + 2 <= end) {
          const roll = i16(b, q);
          f.add('Roll distance', `${roll} samples`, q, 2, 'Negative: decode this many samples first. AAC uses -1 because each frame overlaps the one before.', roll);
          summary = `${gt} · roll distance ${roll}`;
          len ||= 2;
        }
        if (!len) break;
        q += len;
      }
      return { fields: f.list, summary };
    }
    case 'sbgp': {
      const gt = fourcc(b, p);
      f.add('Grouping type', gt, p, 4, 'Which sgpd box these runs point to.');
      let q = p + 4;
      if (version === 1) { f.add('Grouping type parameter', u32be(b, q), q, 4); q += 4; }
      const count = fitCount(u32be(b, q), q + 4, end, 8);
      f.add('Entry count', u32be(b, q), q, 4, 'Number of (sample count, group) runs.');
      return {
        fields: tableFields(f.list, q + 4, count, 8, end, (i, o) => [`Run ${i + 1}`, `${n(u32be(b, o))} samples → group ${u32be(b, o + 4)}`], 'A run of samples that share one group description (0 = none).'),
        summary: `${gt} · ${plural(count, 'run')}`,
      };
    }
    case 'elst': {
      const w = version === 1 ? 8 : 4;
      const es = 2 * w + 4;
      const count = fitCount(u32be(b, p), p + 4, end, es);
      f.add('Entry count', u32be(b, p), p, 4, 'Number of edits. One edit is normal.');
      const mts = ctx.movieTimescale;
      const parts: string[] = [];
      const edits: { q: number; segDur: number; mediaTime: number }[] = [];
      for (let i = 0; i < count; i++) {
        const q = p + 4 + i * es;
        const segDur = w === 8 ? u64be(b, q) : u32be(b, q);
        const mediaTime = w === 8 ? i64(b, q + 8) : i32(b, q + 4);
        t?.edits.push({ segDur, mediaTime });
        edits.push({ q, segDur, mediaTime });
        parts.push(mediaTime === -1 ? `empty ${mts ? fmt3(segDur / mts) + ' s' : ''}` : `skip ${n(mediaTime)}, play ${mts ? fmt3(segDur / mts) + ' s' : n(segDur)}`);
      }
      const head = f.list;
      // Lazy: the media timescale (mdhd) is only known after this box is read.
      const fields = () => {
        const g = new Fields(h.start, end);
        const ts = t?.timescale || 0;
        edits.forEach(({ q, segDur, mediaTime }, i) => {
          g.add(`Edit ${i + 1}: segment duration`, `${n(segDur)} ticks${mts ? ` = ${fmt3(segDur / mts)} s` : ''}`, q, w, i === 0 ? 'How long this edit plays, in the movie timescale (mvhd).' : undefined, segDur);
          g.add(`Edit ${i + 1}: media time`, mediaTime === -1 ? '-1 (empty edit: silence)' : `${n(mediaTime)} ticks${ts ? ` = ${fmt3(mediaTime / ts)} s` : ''}`, q + w, w,
            i === 0 ? 'Where playback starts inside the media. Samples before this point are priming and are not played.' : undefined, mediaTime);
          g.add(`Edit ${i + 1}: rate`, `${i16(b, q + 2 * w)}.${u16be(b, q + 2 * w + 2)}`, q + 2 * w, 4, i === 0 ? 'Playback speed of this edit; 1.0 is normal.' : undefined);
        });
        return [...head, ...g.list];
      };
      return { fields, summary: parts.join('; ') || 'No edits' };
    }
    case 'esds': {
      const res = parseDescriptors(ctx, p, end, 0, depth);
      children.push(...res);
      return { summary: esdsSummary(t?.entries[t.entries.length - 1]) };
    }
    case 'btrt': {
      const buf = u32be(b, p), max = u32be(b, p + 4), avg = u32be(b, p + 8);
      f.add('Buffer size', `${n(buf)} bytes`, p, 4, 'Decoder buffer the stream needs.');
      f.add('Max bitrate', fmtKbps(max), p + 4, 4, 'Highest bitrate over any one-second window.', max);
      f.add('Average bitrate', fmtKbps(avg), p + 8, 4, 'Average bitrate of the whole stream.', avg);
      const e = t?.entries[t.entries.length - 1];
      if (e) { e.avgBitrate ||= avg; e.maxBitrate ||= max; }
      return { fields: f.list, summary: `avg ${fmtKbps(avg)} · max ${fmtKbps(max)}` };
    }
    case 'alac': {
      // The ALAC "magic cookie". Inside an MP4 'alac' entry it is a full box.
      let q = p;
      if (end - p >= 28) {
        f.add('Version', b[q], q, 1);
        f.add('Flags', hex(u24be(b, q + 1), 6), q + 1, 3);
        q += 4;
      }
      const frameLen = u32be(b, q), bps = b[q + 5], chans = b[q + 9], rate = u32be(b, q + 20);
      f.add('Frame length', `${n(frameLen)} samples`, q, 4, 'Samples per channel in each ALAC frame (4,096 by default).', frameLen);
      f.add('Compatible version', b[q + 4], q + 4, 1, 'Always 0.');
      f.add('Bit depth', `${bps} bits`, q + 5, 1, 'Bits per sample of the decoded audio.', bps);
      f.add('Rice history mult (pb)', b[q + 6], q + 6, 1, 'Tuning value for the adaptive Rice coder (normally 40).');
      f.add('Rice initial history (mb)', b[q + 7], q + 7, 1, 'Tuning value for the adaptive Rice coder (normally 10).');
      f.add('Rice parameter limit (kb)', b[q + 8], q + 8, 1, 'Tuning value for the adaptive Rice coder (normally 14).');
      f.add('Channels', chans, q + 9, 1, 'Number of audio channels.', chans);
      f.add('Max run', u16be(b, q + 10), q + 10, 2, 'Unused by the decoder (normally 255).');
      f.add('Max frame bytes', u32be(b, q + 12), q + 12, 4, 'Largest frame in bytes, or 0 if unknown.');
      f.add('Average bitrate', fmtKbps(u32be(b, q + 16)), q + 16, 4, 'Average bitrate, or 0 if unknown.', u32be(b, q + 16));
      f.add('Sample rate', fmtHz(rate), q + 20, 4, 'Samples per second.', rate);
      const e = t?.entries[t.entries.length - 1];
      if (e && e.format === 'alac') {
        e.bitsPerSample = bps; e.channels = chans || e.channels; e.sampleRate = rate || e.sampleRate;
        e.avgBitrate ||= u32be(b, q + 16);
      }
      return { fields: f.list, summary: `${bps}-bit · ${fmtHz(rate)} · ${channelName(chans)} · ${n(frameLen)} samples per frame` };
    }
    case 'dfLa': {
      let q = p;
      let summary = '';
      while (q + 4 <= end) {
        const bf = new BitFields(b, q);
        const last = bf.read('Last block', 1, (x) => (x ? 'Yes' : 'No'), 'Set on the final metadata block.');
        const type = bf.read('Block type', 7, (x) => (x === 0 ? 'STREAMINFO (0)' : String(x)), 'FLAC metadata block type; 0 is STREAMINFO.');
        const len = bf.read('Block length', 24, (x) => `${n(x)} bytes`);
        if (type === 0 && q + 4 + 34 <= end) {
          bf.read('Min block size', 16, (x) => `${n(x)} samples`);
          bf.read('Max block size', 16, (x) => `${n(x)} samples`, 'Largest number of samples in one FLAC frame.');
          bf.read('Min frame size', 24, (x) => `${n(x)} bytes`);
          bf.read('Max frame size', 24, (x) => `${n(x)} bytes`);
          const rate = bf.read('Sample rate', 20, fmtHz);
          const ch = bf.read('Channels', 3, (x) => `${x + 1}`, 'Stored as channels minus 1.') + 1;
          const bps = bf.read('Bits per sample', 5, (x) => `${x + 1}`, 'Stored as bits minus 1.') + 1;
          const hi = bf.bits(4);
          bf.fields.push({ name: 'Total samples', value: n(hi * 4294967296 + bf.peek(32)), bit: bf.pos - 4, bits: 36, note: 'Samples per channel in the whole stream; 0 if unknown.' });
          bf.skip(32);
          bf.fields.push({ name: 'MD5 of audio', value: Array.from(b.subarray(bf.byte, bf.byte + 16), (x) => x.toString(16).padStart(2, '0')).join(''), bit: bf.pos, bits: 128, note: 'A checksum of the decoded audio, to check a decode is exact.' });
          const e = t?.entries[t.entries.length - 1];
          if (e) { e.sampleRate = rate; e.channels = ch; e.bitsPerSample = bps; }
          summary = `${bps}-bit · ${fmtHz(rate)} · ${channelName(ch)}`;
        }
        f.addBits(bf.fields);
        q += 4 + len;
        if (last) break;
      }
      return { fields: f.list, summary };
    }
    case 'dOps': {
      const ch = b[p + 1], preSkip = u16be(b, p + 2), rate = u32be(b, p + 4), gain = i16(b, p + 8), family = b[p + 10];
      f.add('Version', b[p], p, 1, 'Always 0.');
      f.add('Output channels', ch, p + 1, 1, 'Channels after decoding.', ch);
      f.add('Pre-skip', `${n(preSkip)} samples`, p + 2, 2, 'Samples (at 48 kHz) to throw away at the start: the encoder delay.', preSkip);
      f.add('Input sample rate', fmtHz(rate), p + 4, 4, 'Rate of the original audio. Opus itself always decodes at 48 kHz.', rate);
      f.add('Output gain', `${(gain / 256).toFixed(2)} dB`, p + 8, 2, 'Gain to apply when decoding.', gain);
      f.add('Channel mapping family', family, p + 10, 1, '0 = mono/stereo, 1 = surround (Vorbis order).');
      const e = t?.entries[t.entries.length - 1];
      if (e) { e.channels = ch; e.preSkip = preSkip; e.sampleRate = 48000; }
      return { fields: f.list, summary: `${channelName(ch)} · pre-skip ${n(preSkip)}` };
    }
    case 'dac3': {
      const bf = new BitFields(b, p);
      const fscod = bf.read('Sample rate code (fscod)', 2, (x) => fmtHz([48000, 44100, 32000][x] ?? 0), 'Sample rate: 0 = 48 kHz, 1 = 44.1 kHz, 2 = 32 kHz.');
      bf.read('Bitstream ID (bsid)', 5, String, 'Version of the AC-3 syntax; 8 for plain AC-3.');
      bf.read('Bitstream mode (bsmod)', 3, (x) => ['Main audio', 'Music and effects', 'Visually impaired', 'Hearing impaired', 'Dialogue', 'Commentary', 'Emergency', 'Voice over / karaoke'][x], 'The service type.');
      const acmod = bf.read('Channel mode (acmod)', 3, (x) => AC3_ACMOD[x][1], 'Speaker layout of the full-range channels.');
      const lfe = bf.read('LFE on', 1, (x) => (x ? 'Yes' : 'No'), 'Whether a subwoofer (.1) channel is present.');
      const brc = bf.read('Bit rate code', 5, (x) => `${AC3_RATES[x] ?? '?'} kbps`, 'The fixed bitrate of the stream.');
      bf.read('Reserved', 5);
      f.addBits(bf.fields);
      const e = t?.entries[t.entries.length - 1];
      if (e) { e.channels = AC3_ACMOD[acmod][0] + lfe; e.sampleRate = [48000, 44100, 32000][fscod] ?? e.sampleRate; e.avgBitrate = (AC3_RATES[brc] ?? 0) * 1000; }
      return { fields: f.list, summary: `${AC3_ACMOD[acmod][1]}${lfe ? ' + LFE' : ''} · ${AC3_RATES[brc] ?? '?'} kbps` };
    }
    case 'dec3': {
      const bf = new BitFields(b, p);
      const rate = bf.read('Data rate', 13, (x) => `${x} kbps`, 'Total bitrate of the stream.');
      const subs = bf.read('Independent substreams', 3, (x) => `${x + 1}`, 'Stored as count minus 1.') + 1;
      let ch = 0, layout = '';
      for (let i = 0; i < subs && bf.pos + 24 <= end * 8; i++) {
        const fscod = bf.read(`Substream ${i + 1}: fscod`, 2, (x) => fmtHz([48000, 44100, 32000][x] ?? 0), 'Sample rate code.');
        bf.read(`Substream ${i + 1}: bsid`, 5, String, 'Syntax version; 16 for E-AC-3.');
        bf.skip(1);
        bf.read(`Substream ${i + 1}: asvc`, 1);
        bf.read(`Substream ${i + 1}: bsmod`, 3);
        const acmod = bf.read(`Substream ${i + 1}: acmod`, 3, (x) => AC3_ACMOD[x][1], 'Speaker layout.');
        const lfe = bf.read(`Substream ${i + 1}: LFE on`, 1, (x) => (x ? 'Yes' : 'No'));
        bf.skip(3);
        const deps = bf.read(`Substream ${i + 1}: dependent substreams`, 4);
        if (deps) bf.read(`Substream ${i + 1}: channel locations`, 9); else bf.skip(1);
        if (i === 0) {
          ch = AC3_ACMOD[acmod][0] + lfe; layout = AC3_ACMOD[acmod][1] + (lfe ? ' + LFE' : '');
          const e = t?.entries[t.entries.length - 1];
          if (e) { e.channels = ch; e.sampleRate = [48000, 44100, 32000][fscod] ?? e.sampleRate; e.avgBitrate = rate * 1000; }
        }
      }
      f.addBits(bf.fields);
      return { fields: f.list, summary: `${layout} · ${rate} kbps` };
    }
    case 'frma':
      f.add('Original format', fourcc(b, p), p, 4, 'The real codec of the sample entry.');
      return { fields: f.list, summary: fourcc(b, p) };
    case 'chan':
      f.add('Layout tag', hex(u32be(b, p), 8), p, 4, 'A Core Audio channel layout code.');
      return { fields: f.list };
    case 'mehd': {
      const dur = version === 1 ? u64be(b, p) : u32be(b, p);
      f.add('Fragment duration', n(dur), p, version === 1 ? 8 : 4, 'Total length of all fragments, in the movie timescale.');
      return { fields: f.list };
    }
    case 'trex': {
      const id = u32be(b, p);
      f.add('Track ID', id, p, 4, 'The track these defaults apply to.');
      f.add('Default sample description index', u32be(b, p + 4), p + 4, 4);
      f.add('Default sample duration', n(u32be(b, p + 8)), p + 8, 4, 'Used when a fragment does not give a duration.');
      f.add('Default sample size', n(u32be(b, p + 12)), p + 12, 4, 'Used when a fragment does not give a size.');
      f.add('Default sample flags', hex(u32be(b, p + 16), 8), p + 16, 4, 'Key-frame and dependency flags.');
      ctx.trex.set(id, { dur: u32be(b, p + 8), size: u32be(b, p + 12) });
      return { fields: f.list, summary: `Track ${id}` };
    }
    case 'mfhd': {
      f.add('Sequence number', u32be(b, p), p, 4, 'Counts fragments: 1, 2, 3, …');
      return { fields: f.list, summary: `Fragment ${n(u32be(b, p))}` };
    }
    case 'tfhd': {
      const id = u32be(b, p);
      f.add('Track ID', id, p, 4, 'The track this fragment belongs to.');
      const tr = ctx.trex.get(id);
      const st: FragState = { trackId: id, base: ctx.moofStart, defDur: tr?.dur ?? 0, defSize: tr?.size ?? 0, next: ctx.moofStart };
      let q = p + 4;
      if (flags & 1) { st.base = u64be(b, q); f.add('Base data offset', n(st.base), q, 8, 'File position that trun data offsets count from.'); q += 8; }
      if (flags & 2) { f.add('Sample description index', u32be(b, q), q, 4); q += 4; }
      if (flags & 8) { st.defDur = u32be(b, q); f.add('Default sample duration', n(st.defDur), q, 4, 'Ticks per sample unless trun says otherwise.'); q += 4; }
      if (flags & 0x10) { st.defSize = u32be(b, q); f.add('Default sample size', n(st.defSize), q, 4, 'Bytes per sample unless trun says otherwise.'); q += 4; }
      if (flags & 0x20) { f.add('Default sample flags', hex(u32be(b, q), 8), q, 4); q += 4; }
      st.next = st.base;
      ctx.frag = st;
      return { fields: f.list, summary: `Track ${id}` };
    }
    case 'tfdt': {
      const v = version === 1 ? u64be(b, p) : u32be(b, p);
      f.add('Base media decode time', n(v), p, version === 1 ? 8 : 4, 'Decode time of the first sample in this fragment, in media ticks.');
      if (ctx.frag) ctx.frag.decodeTime = v;
      return { fields: f.list, summary: n(v) };
    }
    case 'trun':
      return trunBox(ctx, p, end, flags, f);
    case 'tfra': {
      f.add('Track ID', u32be(b, p), p, 4);
      f.add('Size fields', hex(u32be(b, p + 4), 8), p + 4, 4, 'Byte widths of the traf, trun and sample numbers.');
      f.add('Entry count', u32be(b, p + 8), p + 8, 4, 'How many random access points are listed.');
      return { fields: f.list, summary: plural(u32be(b, p + 8), 'entry') };
    }
    case 'mfro':
      f.add('Size', `${n(u32be(b, p))} bytes`, p, 4, 'Size of the mfra box, so a reader can find it from the file end.');
      return { fields: f.list };
    case 'sidx': {
      const w = version === 0 ? 4 : 8;
      f.add('Reference ID', u32be(b, p), p, 4);
      f.add('Timescale', n(u32be(b, p + 4)), p + 4, 4);
      f.add('Earliest presentation time', n(w === 4 ? u32be(b, p + 8) : u64be(b, p + 8)), p + 8, w);
      f.add('First offset', n(w === 4 ? u32be(b, p + 8 + w) : u64be(b, p + 8 + w)), p + 8 + w, w);
      const count = u16be(b, p + 10 + 2 * w);
      f.add('Reference count', count, p + 10 + 2 * w, 2, 'Number of referenced segments.');
      return { fields: f.list, summary: plural(count, 'reference') };
    }
    case 'iods':
      return { summary: 'MPEG-4 systems profile levels' };
    case 'free':
    case 'skip':
    case 'wide':
      return { summary: fmtBytes(end - h.start) };
  }
  return undefined;
}

const AC3_ACMOD: [number, string][] = [[2, '1+1 (dual mono)'], [1, 'Mono (1/0)'], [2, 'Stereo (2/0)'], [3, '3/0'], [3, '2/1'], [4, '3/1'], [4, '2/2'], [5, '3/2']];
const AC3_RATES = [32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384, 448, 512, 576, 640];

function trunBox(ctx: Ctx, p: number, end: number, flags: number, f: Fields): BoxResult {
  const b = ctx.b;
  const count = u32be(b, p);
  f.add('Sample count', count, p, 4, 'Number of samples in this run.');
  let q = p + 4;
  const st = ctx.frag;
  let dataOff = st ? st.next : 0;
  if (flags & 1) {
    const d = i32(b, q);
    f.add('Data offset', n(d), q, 4, 'Where this run\'s samples start, counted from the base offset (usually the moof start).');
    if (st) dataOff = st.base + d;
    q += 4;
  }
  if (flags & 4) { f.add('First sample flags', hex(u32be(b, q), 8), q, 4); q += 4; }
  const per = ((flags >> 8) & 1) * 4 + ((flags >> 9) & 1) * 4 + ((flags >> 10) & 1) * 4 + ((flags >> 11) & 1) * 4;
  const fit = per ? fitCount(count, q, end, per) : count;
  const tr = st ? ctx.trackById(st.trackId) : undefined;
  if (st && tr) {
    let time = st.decodeTime ?? tr.fragTime;
    let off = dataOff;
    const lim = Math.min(fit, 10_000_000);
    for (let i = 0; i < lim; i++) {
      let o = q + i * per;
      let dur = st.defDur, size = st.defSize;
      if (flags & 0x100) { dur = u32be(b, o); o += 4; }
      if (flags & 0x200) size = u32be(b, o);
      tr.frag.off.push(off); tr.frag.size.push(size); tr.frag.t0.push(time); tr.frag.dur.push(dur);
      tr.frag.entry.push(q + i * per); tr.frag.entryLen.push(per);
      off += size;
      time += dur;
    }
    tr.fragTime = time;
    st.decodeTime = time;
    st.next = off;
  }
  const head = f.list;
  const fieldsFn = per
    ? tableFields(head, q, fit, per, end, (i, o) => {
      const parts: string[] = [];
      if (flags & 0x100) { parts.push(`${n(u32be(b, o))} ticks`); o += 4; }
      if (flags & 0x200) { parts.push(`${n(u32be(b, o))} bytes`); o += 4; }
      if (flags & 0x400) { parts.push(`flags ${hex(u32be(b, o), 8)}`); o += 4; }
      if (flags & 0x800) parts.push(`offset ${i32(b, o)}`);
      return [`Sample ${i + 1}`, parts.join(' · ')];
    }, 'Per-sample values in this run.')
    : () => head;
  return { fields: fieldsFn, summary: plural(count, 'sample') };
}

// ---------------------------------------------------------------------------
// Sample entries (inside stsd)

const AUDIO_FORMATS: Record<string, [string, string, boolean]> = {
  mp4a: ['AAC', 'MPEG-4 Advanced Audio Coding', false],
  alac: ['ALAC', 'Apple Lossless Audio Codec', true],
  fLaC: ['FLAC', 'Free Lossless Audio Codec in MP4', true],
  Opus: ['Opus', 'Opus (IETF RFC 6716) in MP4', false],
  'ac-3': ['AC-3', 'Dolby Digital (AC-3)', false],
  'ec-3': ['E-AC-3', 'Dolby Digital Plus (E-AC-3)', false],
  'ac-4': ['AC-4', 'Dolby AC-4', false],
  '.mp3': ['MP3', 'MPEG-1 Audio Layer III', false],
  samr: ['AMR-NB', 'Adaptive Multi-Rate narrowband speech', false],
  sawb: ['AMR-WB', 'Adaptive Multi-Rate wideband speech', false],
  sowt: ['PCM', 'Linear PCM, little-endian (QuickTime)', true],
  twos: ['PCM', 'Linear PCM, big-endian (QuickTime)', true],
  lpcm: ['PCM', 'Linear PCM (QuickTime)', true],
  ipcm: ['PCM', 'Linear PCM, integer (ISO)', true],
  fpcm: ['PCM', 'Linear PCM, floating point (ISO)', true],
  in24: ['PCM', 'Linear PCM, 24-bit (QuickTime)', true],
  in32: ['PCM', 'Linear PCM, 32-bit (QuickTime)', true],
  fl32: ['PCM', 'Linear PCM, 32-bit float (QuickTime)', true],
  ulaw: ['µ-law', 'G.711 µ-law', false],
  alaw: ['A-law', 'G.711 A-law', false],
  mha1: ['MPEG-H', 'MPEG-H 3D Audio', false],
};
const ENTRY_TYPES = new Set(['mp4a', 'alac', 'fLaC', 'Opus', 'ac-3', 'ec-3']);

function sampleEntry(ctx: Ctx, h: BoxHeader, depth: number): Segment {
  const b = ctx.b;
  const t = ctx.track;
  const hdr = headerSeg(ctx, h, false);
  const p = hdr.body, end = h.end;
  const f = new Fields(h.start, end);
  const known = AUDIO_FORMATS[h.type];
  const isAudio = !!known || t?.handler === 'soun';
  f.add('Reserved', '0', p, 6, 'Six zero bytes.');
  f.add('Data reference index', u16be(b, p + 6), p + 6, 2, 'Which dref entry holds the samples; 1 = this file.');
  const children: Segment[] = [hdr.seg];
  let summary = h.type;
  if (isAudio) {
    const ver = u16be(b, p + 8);
    const chans = u16be(b, p + 16), bits = u16be(b, p + 18), rate = u32be(b, p + 24) / 65536;
    f.add('Version', ver, p + 8, 2, 'QuickTime sound description version: 0, 1 (16 extra bytes) or 2 (36 extra bytes). Always 0 in ISO MP4.');
    f.add('Revision', u16be(b, p + 10), p + 10, 2);
    f.add('Vendor', u32be(b, p + 12) ? fourcc(b, p + 12) : '0', p + 12, 4, 'Encoder vendor code; usually 0.');
    f.add('Channel count', chans, p + 16, 2, 'Number of channels. The codec configuration below has the final word.');
    f.add('Sample size', `${bits} bits`, p + 18, 2, 'Bits per sample; 16 for compressed formats, where it means little.', bits);
    f.add('Compression ID', i16(b, p + 20), p + 20, 2, 'QuickTime field; -2 means variable-size compressed packets.');
    f.add('Packet size', u16be(b, p + 22), p + 22, 2, 'Always 0.');
    f.add('Sample rate', `${fmtHz(rate)} (16.16 fixed point)`, p + 24, 4, 'Samples per second as a 16.16 number, so rates over 65,535 Hz do not fit here.', rate);
    let q = p + 28;
    let entryRate = rate;
    if (ver === 1 && q + 16 <= end) {
      f.add('Samples per packet', n(u32be(b, q)), q, 4, 'QuickTime v1: samples in one compressed packet.');
      f.add('Bytes per packet', n(u32be(b, q + 4)), q + 4, 4);
      f.add('Bytes per frame', n(u32be(b, q + 8)), q + 8, 4);
      f.add('Bytes per sample', n(u32be(b, q + 12)), q + 12, 4);
      q += 16;
    } else if (ver === 2 && q + 36 <= end) {
      const dv = new DataView(b.buffer, b.byteOffset + q + 4, 8);
      entryRate = dv.getFloat64(0);
      f.add('Size of struct', n(u32be(b, q)), q, 4);
      f.add('Sample rate (64-bit float)', fmtHz(entryRate), q + 4, 8, 'QuickTime v2 stores the true rate as a double.');
      f.add('Channels', u32be(b, q + 12), q + 12, 4);
      f.add('Bits per channel', u32be(b, q + 20), q + 20, 4);
      q += 36;
    }
    const [codec, codecLong, lossless] = known ?? [h.type, `Unknown audio format “${h.type}”`, false];
    const entry: EntryInfo = { format: h.type, codec, codecLong, sampleRate: entryRate || undefined, channels: chans || undefined, lossless };
    if (codec === 'PCM') entry.bitsPerSample = bits;
    t?.entries.push(entry);
    children.push(...parseBoxes(ctx, q, end, h.type, depth + 1));
    summary = [entry.codec, entry.sampleRate && fmtHz(entry.sampleRate), entry.channels && channelName(entry.channels), entry.bitsPerSample && `${entry.bitsPerSample}-bit`].filter(Boolean).join(' · ');
  } else if (t?.handler === 'vide' && p + 36 <= end) {
    f.add('Width', `${u16be(b, p + 24)} px`, p + 24, 2, 'Picture width in pixels.');
    f.add('Height', `${u16be(b, p + 26)} px`, p + 26, 2, 'Picture height in pixels.');
    summary = `${h.type} video · ${u16be(b, p + 24)}×${u16be(b, p + 26)}`;
  }
  const type = ENTRY_TYPES.has(h.type) ? `mp4.entry.${h.type}` : isAudio ? 'mp4.entry.audio' : 'mp4.entry.other';
  const label = isAudio ? 'Audio sample entry' : 'Sample entry';
  const seg: Segment = { kind: 'header', type, name: `${h.type} (${label})`, start: h.start, end, summary, children, fields: [...hdr.fields, ...f.list] };
  if (h.warning) seg.warning = h.warning;
  return seg;
}

// ---------------------------------------------------------------------------
// MPEG-4 descriptors (inside esds)

const DESC_TYPES: Record<number, [string, string]> = {
  3: ['mp4.desc.es', 'ES descriptor'],
  4: ['mp4.desc.decoderconfig', 'Decoder config descriptor'],
  5: ['mp4.desc.asc', 'Decoder specific info'],
  6: ['mp4.desc.sl', 'SL config descriptor'],
};
const OTI: Record<number, string> = {
  0x40: 'MPEG-4 Audio (AAC and friends)', 0x66: 'MPEG-2 AAC Main', 0x67: 'MPEG-2 AAC LC', 0x68: 'MPEG-2 AAC SSR',
  0x69: 'MPEG-2 Audio (MP3 at lower rates)', 0x6b: 'MPEG-1 Audio (MP3 / MP2)', 0x6c: 'JPEG image', 0x20: 'MPEG-4 Visual',
  0x21: 'H.264 video', 0xa5: 'AC-3', 0xa6: 'E-AC-3', 0xa9: 'DTS', 0xad: 'Opus', 0xdd: 'Vorbis', 0xe1: 'QCELP',
};
const STREAM_TYPES: Record<number, string> = { 1: 'Object descriptor', 2: 'Clock reference', 3: 'Scene description', 4: 'Visual', 5: 'Audio', 6: 'MPEG-7', 7: 'IPMP', 8: 'OCI', 9: 'MPEG-J' };

function parseDescriptors(ctx: Ctx, p: number, end: number, oti: number, depth: number): Segment[] {
  const out: Segment[] = [];
  while (p + 2 <= end && depth < MAX_DEPTH) {
    const d = parseDescriptor(ctx, p, end, oti, depth);
    out.push(d);
    if (d.end <= p) break;
    p = d.end;
  }
  return out;
}

function parseDescriptor(ctx: Ctx, p: number, limit: number, oti: number, depth: number): Segment {
  const b = ctx.b;
  const tag = b[p];
  // Size is 1–4 bytes, 7 bits each; a set top bit means "another byte follows".
  let q = p + 1, size = 0, nb = 0, byte = 0;
  do {
    byte = b[q++] ?? 0;
    size = size * 128 + (byte & 0x7f);
    nb++;
  } while (byte & 0x80 && nb < 4 && q < limit);
  const end = Math.min(q + size, limit);
  const f = new Fields(p, end);
  const [type, label] = DESC_TYPES[tag] ?? ['mp4.desc.other', `Descriptor (tag ${hex(tag)})`];
  f.add('Tag', `${hex(tag)} (${label})`, p, 1, 'Which kind of descriptor this is.', tag);
  f.add('Size', `${n(size)} bytes`, p + 1, nb, 'Variable-length size: 7 bits per byte, and a set top bit means another byte follows. Encoders often pad it to 4 bytes (80 80 80 xx).', size);
  const children: Segment[] = [];
  let summary = '';
  const t = ctx.track;
  const e = t?.entries[t.entries.length - 1];
  q = p + 1 + nb;
  if (tag === 3) {
    f.add('ES ID', u16be(b, q), q, 2, 'Elementary stream ID; 0 or the track ID.');
    const bf = new BitFields(b, q + 2);
    const dep = bf.read('Stream dependence flag', 1, (x) => (x ? 'Yes' : 'No'), 'Set when this stream depends on another.');
    const url = bf.read('URL flag', 1, (x) => (x ? 'Yes' : 'No'), 'Set when the stream lives at a URL.');
    const ocr = bf.read('OCR stream flag', 1, (x) => (x ? 'Yes' : 'No'));
    bf.read('Stream priority', 5);
    f.addBits(bf.fields);
    q += 3;
    if (dep) { f.add('Depends on ES ID', u16be(b, q), q, 2); q += 2; }
    if (url) { f.add('URL', ascii(b, q + 1, b[q]), q, 1 + b[q]); q += 1 + b[q]; }
    if (ocr) { f.add('OCR ES ID', u16be(b, q), q, 2); q += 2; }
    summary = `ES ID ${u16be(b, p + 1 + nb)}`;
    children.push(...parseDescriptors(ctx, q, end, oti, depth + 1));
  } else if (tag === 4) {
    oti = b[q];
    f.add('Object type indication', `${hex(oti)} (${OTI[oti] ?? 'Unknown'})`, q, 1, 'The codec family. 0x40 means MPEG-4 Audio, which covers AAC.', oti);
    const bf = new BitFields(b, q + 1);
    bf.read('Stream type', 6, (x) => `${STREAM_TYPES[x] ?? 'Unknown'} (${x})`, '5 means audio.');
    bf.read('Upstream', 1, (x) => (x ? 'Yes' : 'No'), 'Always 0 for files.');
    bf.read('Reserved', 1);
    f.addBits(bf.fields);
    const buf = u24be(b, q + 2), max = u32be(b, q + 5), avg = u32be(b, q + 9);
    f.add('Buffer size', `${n(buf)} bytes`, q + 2, 3, 'Decoder input buffer size the stream needs.', buf);
    f.add('Max bitrate', max ? fmtKbps(max) : '0 (not given)', q + 5, 4, 'Peak bitrate. Some encoders just write the average here.', max);
    f.add('Average bitrate', avg ? fmtKbps(avg) : '0 (variable / not given)', q + 9, 4, 'Average bitrate; 0 often means variable bitrate.', avg);
    if (e) {
      e.oti = oti;
      if (avg) e.avgBitrate = avg;
      if (max) e.maxBitrate = max;
      if (oti === 0x6b || oti === 0x69) { e.codec = 'MP3'; e.codecLong = 'MPEG Audio Layer III in MP4'; }
      else if (oti === 0xa5) { e.codec = 'AC-3'; e.codecLong = 'Dolby Digital (AC-3)'; }
    }
    summary = `${OTI[oti] ?? hex(oti)}${avg ? ' · ' + fmtKbps(avg) : ''}`;
    children.push(...parseDescriptors(ctx, q + 13, end, oti, depth + 1));
  } else if (tag === 5 && [0x40, 0x66, 0x67, 0x68].includes(oti)) {
    const asc = parseAsc(b, q, end);
    f.addBits(asc.fields);
    if (e) applyAsc(e, asc, oti);
    summary = e ? `${e.codec} · ${fmtHz(e.sampleRate ?? 0)} · ${channelName(e.channels ?? 0)}` : aotName(asc.aot);
    return { kind: 'header', type, name: 'AudioSpecificConfig', start: p, end, fields: f.list, summary };
  } else if (tag === 6) {
    f.add('Predefined', `${b[q]}${b[q] === 2 ? ' (MP4 file)' : ''}`, q, 1, 'A preset for packet headers; 2 is the fixed value for MP4 files.');
    summary = `Predefined ${b[q]}`;
  } else if (end > q) {
    f.add('Data', `${n(end - q)} bytes`, q, end - q);
  }
  return { kind: 'header', type, name: label, start: p, end, fields: f.list, summary, children: children.length ? children : undefined };
}

function applyAsc(e: EntryInfo, asc: AscInfo, oti: number): void {
  e.asc = asc;
  let sbr = asc.sbr;
  // Implicit SBR: nothing in the config, but the sample entry runs at twice the core rate.
  if (!sbr && asc.aot === 2 && e.sampleRate && e.sampleRate === asc.sampleRate * 2) sbr = true;
  const aac = oti === 0x40 ? '' : 'MPEG-2 ';
  if (asc.aot === 2 && asc.ps) { e.codec = 'HE-AAC v2'; e.codecLong = `${aac}High-Efficiency AAC v2 (AAC-LC + SBR + Parametric Stereo)`; }
  else if (asc.aot === 2 && sbr) { e.codec = 'HE-AAC'; e.codecLong = `${aac}High-Efficiency AAC (AAC-LC + Spectral Band Replication)`; }
  else if (asc.aot === 2) { e.codec = 'AAC-LC'; e.codecLong = `${aac}Advanced Audio Coding, Low Complexity profile`; }
  else if (asc.aot === 1) { e.codec = 'AAC Main'; e.codecLong = 'Advanced Audio Coding, Main profile'; }
  else if (asc.aot === 23) { e.codec = 'AAC-LD'; e.codecLong = 'AAC Low Delay'; }
  else if (asc.aot === 39) { e.codec = 'AAC-ELD'; e.codecLong = 'AAC Enhanced Low Delay'; }
  else if (asc.aot === 42) { e.codec = 'xHE-AAC'; e.codecLong = 'Extended HE-AAC (USAC)'; }
  else if (asc.aot === 34) { e.codec = 'MP3'; e.codecLong = 'MPEG Layer-3 in MP4'; }
  else { e.codec = AOT_NAMES[asc.aot]?.replace(/ \(.*\)$/, '') ?? 'AAC'; e.codecLong = `MPEG-4 Audio, ${aotName(asc.aot)}`; }
  const base = asc.sampleRate;
  if (base) e.sampleRate = sbr ? asc.extSampleRate || base * 2 : base;
  const ch = AAC_CHANNELS[asc.channelConfig]?.[0];
  if (asc.ps) e.channels = 2;
  else if (ch) e.channels = ch;
}

function esdsSummary(e: EntryInfo | undefined): string {
  if (!e) return '';
  return [e.codec, e.sampleRate && fmtHz(e.sampleRate), e.channels && channelName(e.channels), e.avgBitrate && fmtKbps(e.avgBitrate)].filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------------
// iTunes metadata (ilst) and QuickTime text atoms

const ILST_NAMES: Record<string, string> = {
  '©nam': 'Title', '©ART': 'Artist', aART: 'Album artist', '©alb': 'Album', '©day': 'Year', trkn: 'Track', disk: 'Disc',
  '©gen': 'Genre', gnre: 'Genre', '©cmt': 'Comment', '©too': 'Encoder', '©wrt': 'Composer', cpil: 'Compilation',
  tmpo: 'BPM', covr: 'Cover art', '©lyr': 'Lyrics', cprt: 'Copyright', '©grp': 'Grouping', desc: 'Description',
  ldes: 'Long description', '©enc': 'Encoded by', '©swr': 'Encoder', pgap: 'Gapless album', stik: 'Media kind',
  rtng: 'Rating', soal: 'Sort album', soar: 'Sort artist', sonm: 'Sort title', soaa: 'Sort album artist',
  soco: 'Sort composer', '©pub': 'Publisher', '©mvn': 'Movement', '©wrk': 'Work', purd: 'Purchase date', '©st3': 'Subtitle',
  '©req': 'Requirements', '©inf': 'Information', '©dir': 'Director', '©prd': 'Producer', '©com': 'Composer', '©aut': 'Author',
};

const ID3_GENRES = ['Blues', 'Classic Rock', 'Country', 'Dance', 'Disco', 'Funk', 'Grunge', 'Hip-Hop', 'Jazz', 'Metal', 'New Age',
  'Oldies', 'Other', 'Pop', 'R&B', 'Rap', 'Reggae', 'Rock', 'Techno', 'Industrial', 'Alternative', 'Ska', 'Death Metal', 'Pranks',
  'Soundtrack', 'Euro-Techno', 'Ambient', 'Trip-Hop', 'Vocal', 'Jazz+Funk', 'Fusion', 'Trance', 'Classical', 'Instrumental', 'Acid',
  'House', 'Game', 'Sound Clip', 'Gospel', 'Noise', 'Alternative Rock', 'Bass', 'Soul', 'Punk', 'Space', 'Meditative',
  'Instrumental Pop', 'Instrumental Rock', 'Ethnic', 'Gothic', 'Darkwave', 'Techno-Industrial', 'Electronic', 'Pop-Folk',
  'Eurodance', 'Dream', 'Southern Rock', 'Comedy', 'Cult', 'Gangsta', 'Top 40', 'Christian Rap', 'Pop/Funk', 'Jungle',
  'Native American', 'Cabaret', 'New Wave', 'Psychedelic', 'Rave', 'Showtunes', 'Trailer', 'Lo-Fi', 'Tribal', 'Acid Punk',
  'Acid Jazz', 'Polka', 'Retro', 'Musical', 'Rock & Roll', 'Hard Rock'];

const DATA_TYPES: Record<number, string> = {
  0: 'Binary (implicit)', 1: 'UTF-8 text', 2: 'UTF-16 text', 4: 'Mac text', 13: 'JPEG image', 14: 'PNG image',
  21: 'Signed integer', 22: 'Unsigned integer', 23: 'Float', 24: 'Double', 27: 'BMP image', 28: 'Nested metadata',
};

function ilstItem(ctx: Ctx, h: BoxHeader, depth: number): Segment {
  const b = ctx.b;
  const key = h.type;
  const hdr = headerSeg(ctx, h, false);
  const children: Segment[] = [hdr.seg];
  let mean = '', name = '';
  const values: string[] = [];
  let image: Segment['image'] | undefined;
  let p = hdr.body;
  while (p < h.end && depth < MAX_DEPTH) {
    const ch = readHeader(b, p, h.end);
    if (!ch) {
      children.push({ kind: 'unknown', type: 'mp4.invalid', name: 'Unreadable bytes', start: p, end: h.end, warning: 'These bytes do not form a valid box.' });
      break;
    }
    const chdr = headerSeg(ctx, ch, ch.type !== 'data');
    const f = new Fields(ch.start, ch.end);
    const q = chdr.body;
    let seg: Segment;
    if (ch.type === 'mean' || ch.type === 'name') {
      const s = utf8Str(b, q, ch.end - q);
      if (ch.type === 'mean') mean = s; else name = s;
      f.add(ch.type === 'mean' ? 'Namespace' : 'Name', s, q, ch.end - q, ch.type === 'mean' ? 'Who defined this tag, as a reverse domain name.' : 'The tag name inside that namespace.');
      seg = { kind: 'metadata', type: `mp4.ilst.${ch.type}`, name: `${ch.type} (${ch.type === 'mean' ? 'Namespace' : 'Name'})`, start: ch.start, end: ch.end, summary: s, children: [chdr.seg], fields: [...chdr.fields, ...f.list] };
    } else if (ch.type === 'data') {
      // 'data' has a 4-byte type indicator (1 byte reserved + 3 byte type) and a 4-byte locale.
      const dtype = u24be(b, q + 1);
      f.add('Type indicator', `${dtype} (${DATA_TYPES[dtype] ?? 'Unknown'})`, q, 4, 'How to read the value: 1 = UTF-8 text, 13 = JPEG, 14 = PNG, 21 = integer, 0 = binary.', dtype);
      f.add('Locale', u32be(b, q + 4), q + 4, 4, 'Country and language of the value; 0 means default.');
      const v = q + 8, len = Math.max(0, ch.end - v);
      let kind: SegmentKind = 'metadata', type = 'mp4.ilst.data', summary = '';
      let dataImage: Segment['image'] | undefined;
      const mime = dtype === 13 ? 'image/jpeg' : dtype === 14 ? 'image/png' : dtype === 27 ? 'image/bmp' : sniffImage(b, v);
      if (key === 'covr' || (mime && dtype !== 1)) {
        const m = mime || 'image/jpeg';
        kind = 'image';
        type = 'mp4.ilst.image';
        dataImage = { mime: m, start: v, end: v + len };
        image ??= dataImage;
        ctx.pictures.push({ mime: m, start: v, end: v + len, pictureType: 'Front cover' });
        summary = `${m.replace('image/', '').toUpperCase()} · ${fmtBytes(len)}`;
        values.push(summary);
        f.add('Image data', summary, v, len, 'The raw image file bytes.');
      } else {
        const val = dataValue(b, key, dtype, v, len);
        values.push(val);
        summary = val.length > 80 ? val.slice(0, 80) + '…' : val;
        f.add('Value', val.length > 2000 ? val.slice(0, 2000) + '…' : val, v, len, valueNote(key, dtype));
      }
      seg = { kind, type, name: 'data (Value)', start: ch.start, end: ch.end, summary, children: [chdr.seg], fields: [...chdr.fields, ...f.list] };
      if (dataImage) seg.image = dataImage;
    } else {
      seg = { kind: 'unknown', type: 'mp4.box.unknown', name: ch.type, start: ch.start, end: ch.end, children: [chdr.seg], fields: chdr.fields };
    }
    if (ch.warning) seg.warning = ch.warning;
    children.push(seg);
    p = ch.end;
  }
  const value = values.join('; ');
  const freeform = key === '----';
  const label = freeform ? name || 'Freeform' : ILST_NAMES[key] ?? 'Custom tag';
  if (key !== 'covr' && values.length) {
    ctx.tags.push({ key: freeform ? name || 'Freeform' : ILST_NAMES[key] ?? key, value, rawKey: freeform ? `----:${mean}:${name}` : key });
    if (key === '©too' || (key === '©swr' && !ctx.encoder)) ctx.encoder = value;
    if (freeform && name === 'iTunSMPB') {
      // " 00000000 00000840 000001CA 00000000000FBBF4 ..." → delay, padding, length (hex).
      const parts = value.trim().split(/\s+/).map((x) => parseInt(x, 16));
      if (parts.length >= 4 && parts.slice(1, 4).every((x) => Number.isFinite(x))) ctx.smpb = { delay: parts[1], padding: parts[2], length: parts[3] };
    }
  }
  const seg: Segment = {
    kind: key === 'covr' ? 'image' : 'metadata',
    type: freeform ? 'mp4.ilst.freeform' : key === 'covr' ? 'mp4.ilst.cover' : 'mp4.ilst.item',
    name: `${key} (${label})`, start: h.start, end: h.end, summary: value.length > 80 ? value.slice(0, 80) + '…' : value,
    children, fields: hdr.fields,
  };
  if (image) seg.image = image;
  if (h.warning) seg.warning = h.warning;
  return seg;
}

function sniffImage(b: Uint8Array, p: number): string {
  if (b[p] === 0xff && b[p + 1] === 0xd8) return 'image/jpeg';
  if (b[p] === 0x89 && b[p + 1] === 0x50 && b[p + 2] === 0x4e && b[p + 3] === 0x47) return 'image/png';
  return '';
}

function beInt(b: Uint8Array, p: number, len: number, signed: boolean): number {
  let v = 0;
  for (let i = 0; i < len; i++) v = v * 256 + b[p + i];
  if (signed && len > 0 && len <= 6 && b[p] & 0x80) v -= 2 ** (8 * len);
  return v;
}

function dataValue(b: Uint8Array, key: string, dtype: number, p: number, len: number): string {
  if (key === 'trkn' || key === 'disk') {
    if (len >= 6) {
      const num = u16be(b, p + 2), total = u16be(b, p + 4);
      return total ? `${num}/${total}` : String(num);
    }
  }
  if (key === 'gnre' && len >= 2) {
    const g = u16be(b, p);
    return ID3_GENRES[g - 1] ?? `Genre #${g}`;
  }
  if (key === 'cpil' || key === 'pgap') return b[p] ? 'Yes' : 'No';
  switch (dtype) {
    case 1: return utf8Str(b, p, len);
    case 2: return utf16Str(b, p, len, true);
    case 21: return len <= 8 ? String(beInt(b, p, len, true)) : `${n(len)} bytes`;
    case 22: return len <= 8 ? String(beInt(b, p, len, false)) : `${n(len)} bytes`;
  }
  if (len <= 32) return Array.from(b.subarray(p, p + len), (x) => x.toString(16).padStart(2, '0')).join(' ') || '(empty)';
  return `${n(len)} bytes of binary data`;
}

function valueNote(key: string, dtype: number): string {
  if (key === 'trkn') return 'Binary: 2 zero bytes, track number, total tracks, 2 zero bytes.';
  if (key === 'disk') return 'Binary: 2 zero bytes, disc number, total discs.';
  if (key === 'gnre') return 'An ID3v1 genre number plus 1.';
  if (dtype === 1) return 'UTF-8 text with no terminator; the box size gives the length.';
  return 'The tag value.';
}

/** QuickTime-style udta text atom: 2-byte length, 2-byte language, text. */
function qtTextAtom(ctx: Ctx, h: BoxHeader): Segment {
  const b = ctx.b;
  const hdr = headerSeg(ctx, h, false);
  const p = hdr.body;
  const f = new Fields(h.start, h.end);
  const len = Math.min(u16be(b, p), Math.max(0, h.end - p - 4));
  const text = utf8Str(b, p + 4, len);
  f.add('Text length', len, p, 2, 'Length of the text in bytes.');
  f.add('Language', u16be(b, p + 2) < 0x400 ? `Mac code ${u16be(b, p + 2)}` : language(u16be(b, p + 2)), p + 2, 2, 'Packed language code of the text.');
  f.add('Text', text, p + 4, len, 'The tag value.');
  const key = h.type;
  ctx.tags.push({ key: ILST_NAMES[key] ?? key, value: text, rawKey: key });
  if ((key === '©too' || key === '©swr') && !ctx.encoder) ctx.encoder = text;
  return { kind: 'metadata', type: 'mp4.udta.text', name: `${key} (${ILST_NAMES[key] ?? 'Text'})`, start: h.start, end: h.end, summary: text, children: [hdr.seg], fields: [...hdr.fields, ...f.list] };
}

// ---------------------------------------------------------------------------
// Samples

/** Rebuilds per-sample offsets, sizes and times from stsz/stsc/stco/stts. */
function buildSampleTable(b: Uint8Array, t: Track): SampleTable | undefined {
  if (t.frag.off.length) {
    const fr = t.frag;
    return {
      count: fr.off.length, off: Float64Array.from(fr.off), size: Uint32Array.from(fr.size), t0: Float64Array.from(fr.t0), dur: Float64Array.from(fr.dur),
      entryOff: Float64Array.from(fr.entry), entryLen: Uint8Array.from(fr.entryLen),
    };
  }
  const sz = t.stsz, co = t.stco, sc = t.stsc;
  if (!sz || !co || !sc || !sz.count) return undefined;
  let count = sz.count;
  const size = new Uint32Array(count);
  if (sz.constSize) size.fill(sz.constSize);
  else if (sz.fieldBits === 32) for (let i = 0; i < count; i++) size[i] = u32be(b, sz.off + i * 4);
  else if (sz.fieldBits === 16) for (let i = 0; i < count; i++) size[i] = u16be(b, sz.off + i * 2);
  else if (sz.fieldBits === 8) for (let i = 0; i < count; i++) size[i] = b[sz.off + i];
  else for (let i = 0; i < count; i++) size[i] = (b[sz.off + (i >> 1)] >> (i & 1 ? 0 : 4)) & 15;

  // Walk chunks: stsc says how many samples each chunk holds; stco says where it starts.
  const off = new Float64Array(count);
  const chunks = co.count;
  let s = 0;
  for (let e = 0; e < sc[1] && s < count; e++) {
    const q = sc[0] + e * 12;
    const first = Math.max(1, u32be(b, q)), per = u32be(b, q + 4);
    const next = e + 1 < sc[1] ? u32be(b, q + 12) : chunks + 1;
    for (let c = first; c < next && c <= chunks && s < count; c++) {
      let pos = co.wide ? u64be(b, co.off + (c - 1) * 8) : u32be(b, co.off + (c - 1) * 4);
      for (let k = 0; k < per && s < count; k++) {
        off[s] = pos;
        pos += size[s];
        s++;
      }
    }
  }
  count = s;

  // Times: stts is a list of (run length, duration) pairs.
  const t0 = new Float64Array(count), dur = new Float64Array(count);
  let i = 0, tick = 0, last = 0;
  if (t.stts) {
    for (let e = 0; e < t.stts[1] && i < count; e++) {
      const q = t.stts[0] + e * 8;
      const c = u32be(b, q);
      last = u32be(b, q + 4);
      for (let k = 0; k < c && i < count; k++, i++) {
        t0[i] = tick;
        dur[i] = last;
        tick += last;
      }
    }
  }
  for (; i < count; i++) { t0[i] = tick; dur[i] = last; tick += last; }
  return { count, off: off.subarray(0, count), size: size.subarray(0, count), t0, dur };
}

const AAC_ELEMENTS = ['SCE (single channel element)', 'CPE (channel pair element)', 'CCE (coupling channel element)', 'LFE (low-frequency element)',
  'DSE (data stream element)', 'PCE (program config element)', 'FIL (fill element)', 'END (end of frame)'];

/** A few header bits at the start of one sample, per codec. */
function sampleFields(b: Uint8Array, format: string, start: number, end: number): Field[] {
  if (end <= start) return [];
  const bf = new BitFields(b, start);
  if (format === 'mp4a') {
    const id = bf.read('First element (id_syn_ele)', 3, (x) => AAC_ELEMENTS[x], 'A raw AAC frame is a list of syntax elements. Each starts with this 3-bit id. Stereo frames usually start with a CPE.');
    if (id !== 7) bf.read('Element instance tag', 4, String, 'Tells elements of the same type apart.');
    if (id === 0 || id === 3) bf.read('Global gain', 8, String, 'The starting scale factor for this channel.');
    else if (id === 1) bf.read('Common window', 1, (x) => (x ? 'Yes' : 'No'), 'Both channels share one window shape, which allows mid/side stereo.');
    else if (id === 4) { bf.read('Byte align flag', 1); bf.read('Count', 8); }
    else if (id === 6) {
      // Fill elements reuse the tag bits as a count (4 bits, 15 = escape).
      bf.pos -= 4;
      bf.fields.pop();
      const c = bf.read('Count', 4, String, 'Number of fill bytes; 15 means an 8-bit extra count follows.');
      if (c === 15) bf.read('Extra count', 8, String, 'Total fill bytes = 15 + this - 1.');
    }
  } else if (format === 'alac') {
    const tag = bf.read('Element type', 3, (x) => ['SCE (mono)', 'CPE (stereo pair)', 'CCE', 'LFE', 'DSE', 'PCE', 'FIL', 'END'][x], 'ALAC borrows AAC\'s element ids. A stereo frame starts with a CPE.');
    if (tag !== 7) {
      bf.read('Element instance tag', 4);
      bf.read('Unused', 12, String, 'Always zero.');
      const partial = bf.read('Partial frame', 1, (x) => (x ? 'Yes (sample count follows)' : 'No (full frame)'), 'Set on the last frame when it is shorter than normal.');
      bf.read('Bytes shifted', 2, String, 'Low bytes stored raw instead of predicted (used for 24-bit audio).');
      const esc = bf.read('Uncompressed', 1, (x) => (x ? 'Yes (raw PCM)' : 'No (compressed)'), 'ALAC stores a frame raw when compression would make it bigger.');
      if (partial) bf.read('Sample count', 32, (x) => n(x), 'Samples in this short frame.');
      if (!esc && tag === 1) {
        bf.read('Mix bits', 8, String, 'Stereo decorrelation shift.');
        bf.read('Mix residual', 8, String, 'Stereo decorrelation weight; 0 means plain left/right.');
      }
    }
  } else if (format === 'fLaC') {
    bf.read('Sync code', 14, (x) => hex(x, 4), 'Every FLAC frame starts with 0b11111111111110.');
    bf.read('Reserved', 1);
    bf.read('Blocking strategy', 1, (x) => (x ? 'Variable block size' : 'Fixed block size'));
    bf.read('Block size code', 4, String, 'Encodes the number of samples in the frame.');
    bf.read('Sample rate code', 4, String);
    bf.read('Channel assignment', 4, (x) => (x < 8 ? `${x + 1} independent` : ['Left/side', 'Right/side', 'Mid/side'][x - 8] ?? 'Reserved'), 'How the channels are stored, including stereo decorrelation.');
  } else if (format === 'Opus') {
    bf.read('Config', 5, (x) => (x < 12 ? `SILK (${x})` : x < 16 ? `Hybrid (${x})` : `CELT (${x})`), 'The TOC byte: mode, bandwidth and frame length in one number.');
    bf.read('Stereo', 1, (x) => (x ? 'Yes' : 'No'));
    bf.read('Frame count code', 2, (x) => ['1 frame', '2 equal frames', '2 different frames', 'Any number of frames'][x]);
  } else if (format === 'ac-3') {
    bf.read('Sync word', 16, (x) => hex(x, 4), 'Every AC-3 frame starts with 0x0B77.');
    bf.read('CRC 1', 16, (x) => hex(x, 4));
    bf.read('Sample rate code', 2, (x) => fmtHz([48000, 44100, 32000][x] ?? 0));
    bf.read('Frame size code', 6, String, 'With the rate code, gives the frame length and bitrate.');
  } else if (format === 'ec-3') {
    bf.read('Sync word', 16, (x) => hex(x, 4), 'Every E-AC-3 frame starts with 0x0B77.');
    bf.read('Stream type', 2, (x) => ['Independent', 'Dependent', 'AC-3 converted', 'Reserved'][x]);
    bf.read('Substream ID', 3);
    bf.read('Frame size', 11, (x) => `${(x + 1) * 2} bytes`, 'Stored as 16-bit words minus 1.');
  }
  return bf.fields.filter((f) => f.bit + f.bits <= end * 8);
}

/** Facts shared by all samples of one track, so each sample holds only one reference. */
interface SampleGroup {
  b: Uint8Array;
  format: string;
  /** Name prefix, e.g. "Sample" or "Video sample". */
  label: string;
  /** Name suffix, e.g. " (track 2)". */
  suffix: string;
  table: SampleTable;
  timescale: number;
  /** Only the mapped audio track carries times. */
  timed: boolean;
  track: Track;
  /** First sample number of each stsc / stts run, built on the first links() call. */
  stscStart?: Float64Array;
  sttsStart?: Float64Array;
}

/** Index of the last run whose first sample is <= i (binary search). */
function runOf(starts: Float64Array, i: number): number {
  let lo = 0, hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= i) lo = mid; else hi = mid - 1;
  }
  return lo;
}

/** The table entries that describe sample i: its size, its chunk's offset, its chunk run, its duration. */
function sampleLinks(g: SampleGroup, i: number): SegmentLink[] {
  const b = g.b, t = g.track, st = g.table;
  const links: SegmentLink[] = [];
  if (st.entryOff && st.entryLen) {
    if (st.entryLen[i]) links.push({ label: 'entry in trun', start: st.entryOff[i], end: st.entryOff[i] + st.entryLen[i] });
    return links;
  }
  const sz = t.stsz;
  if (sz) {
    const name = sz.fieldBits === 32 && !sz.constSize ? 'stsz' : 'stz2';
    if (sz.constSize) links.push({ label: 'size in stsz (same for all)', start: sz.off - 8, end: sz.off - 4 });
    else if (sz.fieldBits === 4) links.push({ label: `size in ${name}`, start: sz.off + (i >> 1), end: sz.off + (i >> 1) + 1 });
    else links.push({ label: `size in ${name}`, start: sz.off + (i * sz.fieldBits) / 8, end: sz.off + ((i + 1) * sz.fieldBits) / 8 });
  }
  const sc = t.stsc, co = t.stco;
  if (sc && co && sc[1]) {
    if (!g.stscStart) {
      // Mirrors the chunk walk in buildSampleTable.
      g.stscStart = new Float64Array(sc[1]);
      let acc = 0;
      for (let e = 0; e < sc[1]; e++) {
        const q = sc[0] + e * 12;
        const first = Math.max(1, u32be(b, q));
        const next = Math.min(e + 1 < sc[1] ? u32be(b, q + 12) : co.count + 1, co.count + 1);
        g.stscStart[e] = acc;
        acc += Math.max(0, next - first) * u32be(b, q + 4);
      }
    }
    const e = runOf(g.stscStart, i);
    const q = sc[0] + e * 12;
    const per = u32be(b, q + 4) || 1;
    const chunk = Math.max(1, u32be(b, q)) - 1 + Math.floor((i - g.stscStart[e]) / per);
    links.push({ label: 'samples per chunk in stsc', start: q, end: q + 12 });
    if (chunk < co.count) {
      const w = co.wide ? 8 : 4;
      links.push({ label: `chunk offset in ${co.wide ? 'co64' : 'stco'}`, start: co.off + chunk * w, end: co.off + (chunk + 1) * w });
    }
  }
  const tt = t.stts;
  if (tt && tt[1]) {
    if (!g.sttsStart) {
      g.sttsStart = new Float64Array(tt[1]);
      let acc = 0;
      for (let e = 0; e < tt[1]; e++) { g.sttsStart[e] = acc; acc += u32be(b, tt[0] + e * 8); }
    }
    const e = runOf(g.sttsStart, i);
    links.push({ label: 'duration in stts', start: tt[0] + e * 8, end: tt[0] + e * 8 + 8 });
  }
  return links;
}

/**
 * One sample. Name, time, summary and fields are computed on demand (prototype
 * getters; time is cached after the first read), so a million samples cost
 * one small object each.
 */
class SampleSegment implements Segment {
  readonly #g: SampleGroup;
  readonly #i: number;
  #time: [number, number] | undefined;
  constructor(public kind: SegmentKind, public type: string, public start: number, public end: number, g: SampleGroup, i: number) {
    this.#g = g;
    this.#i = i;
  }
  get time(): [number, number] | undefined {
    const g = this.#g;
    if (!this.#time && g.timed) {
      const t0 = g.table.t0[this.#i] / g.timescale;
      this.#time = [t0, t0 + g.table.dur[this.#i] / g.timescale];
    }
    return this.#time;
  }
  get name(): string {
    return `${this.#g.label} ${n(this.#i + 1)}${this.#g.suffix}`;
  }
  get summary(): string {
    const ms = (this.#g.table.dur[this.#i] / this.#g.timescale) * 1000;
    return `${n(this.end - this.start)} bytes · ${ms.toFixed(1)} ms`;
  }
  get fields(): () => Field[] {
    return () => sampleFields(this.#g.b, this.#g.format, this.start, this.end);
  }
  get links(): () => SegmentLink[] {
    return () => sampleLinks(this.#g, this.#i);
  }
}

// ---------------------------------------------------------------------------
// Top level

function parse(b: Uint8Array): ParsedFile {
  const ctx = new Ctx(b);
  const segments = parseBoxes(ctx, 0, b.length, '', 0);
  const notes = ctx.notes;

  for (const t of ctx.tracks) {
    try { t.samples = buildSampleTable(b, t); } catch { t.samples = undefined; }
  }
  const audio = ctx.tracks.find((t) => t.handler === 'soun' && t.samples?.count) ?? ctx.tracks.find((t) => t.handler === 'soun');
  const entry = audio?.entries[0];

  // Build sample segments for every track and attach them to their mdat.
  const all: SampleSegment[] = [];
  for (const t of ctx.tracks) {
    const st = t.samples;
    if (!st) continue;
    const ts = t.timescale || 1;
    const format = t.entries[0]?.format ?? '';
    if (t === audio) {
      const g: SampleGroup = { b, format, label: 'Sample', suffix: '', table: st, timescale: ts, timed: true, track: t };
      for (let i = 0; i < st.count; i++) all.push(new SampleSegment('audio', 'mp4.sample', st.off[i], st.off[i] + st.size[i], g, i));
    } else {
      const still = t.handler === 'vide' && st.count <= 2 ? 'still image' : `track ${t.id}`;
      const g: SampleGroup = { b, format, label: `${HANDLERS[t.handler] ?? 'Other'} sample`, suffix: ` (${still})`, table: st, timescale: ts, timed: false, track: t };
      const kind: SegmentKind = t.handler === 'soun' ? 'audio' : 'unknown';
      for (let i = 0; i < st.count; i++) all.push(new SampleSegment(kind, 'mp4.sample.other', st.off[i], st.off[i] + st.size[i], g, i));
    }
  }
  let sorted = true;
  for (let i = 1; i < all.length && sorted; i++) if (all[i].start < all[i - 1].start) sorted = false;
  if (!sorted) all.sort((x, y) => x.start - y.start);

  const mdats = ctx.mdats.filter((m) => m && m.children).sort((x, y) => x.start - y.start);
  const frames: Segment[] = [];
  let dropped = 0, mi = 0, prevEnd = 0;
  for (const s of all) {
    while (mi < mdats.length && s.start >= mdats[mi].end) { mi++; prevEnd = 0; }
    const m = mdats[mi];
    const kids = m?.children as Segment[] | undefined;
    const bodyStart = kids?.[0]?.end ?? 0;
    if (!m || !kids || s.start < bodyStart || s.end > m.end || s.end <= s.start || s.start < prevEnd) { dropped++; continue; }
    kids.push(s);
    prevEnd = s.end;
    if (s.type === 'mp4.sample') frames.push(s);
  }
  if (dropped) notes.push({ level: 'warn', text: `${plural(dropped, 'sample')} point outside the media data, overlap other samples, or are empty. They are not shown.` });
  for (const m of mdats) {
    const count = (m.children as Segment[]).length - 1;
    if (count) m.summary = `${fmtBytes(m.end - m.start)} · ${plural(count, 'sample')}`;
    const kids = m.children as Segment[];
    const fr = kids.find((c) => c.time);
    let lastFr: Segment | undefined;
    for (let i = kids.length - 1; i > 0 && !lastFr; i--) if (kids[i].time) lastFr = kids[i];
    if (fr?.time && lastFr?.time) m.time = [fr.time[0], lastFr.time[1]];
  }

  const info = audioInfo(ctx, audio, entry, frames);
  addNotes(ctx, audio, entry, info, frames);
  if (!audio && !frames.length && info.codec === 'Unknown') {
    notes.push({ level: 'warn', text: ctx.moovStart < 0 ? 'No moov box was found, so the layout of the audio is unknown. The file may be cut short.' : 'No audio track was found in this file.' });
  }

  return {
    format: 'mp4',
    container: containerName(ctx.brand),
    size: b.length,
    segments,
    frames,
    audio: info,
    tags: ctx.tags,
    pictures: ctx.pictures,
    notes,
  };
}

function containerName(brand?: string): string {
  switch (brand?.trim()) {
    case 'M4A': return 'MPEG-4 (M4A)';
    case 'M4B': return 'MPEG-4 (M4B audiobook)';
    case 'M4P': return 'MPEG-4 (M4P, protected)';
    case 'qt': return 'QuickTime (MOV)';
    case 'dash': case 'msdh': case 'iso5': case 'iso6': return 'MPEG-4 (fragmented)';
    case '3gp4': case '3gp5': case '3gp6': case '3g2a': return '3GPP';
    default: return 'MPEG-4 (MP4)';
  }
}

function audioInfo(ctx: Ctx, t: Track | undefined, e: EntryInfo | undefined, frames: Segment[]): AudioInfo {
  if (!t || !e) return { codec: t ? 'Unknown' : 'Unknown', lossless: false };
  const info: AudioInfo = { codec: e.codec, codecLong: e.codecLong, lossless: e.lossless };
  info.sampleRate = e.sampleRate ? Math.round(e.sampleRate) : undefined;
  info.channels = e.channels;
  if (e.channels) info.channelMode = channelName(e.channels);
  if (e.lossless) info.bitsPerSample = e.bitsPerSample;
  if (ctx.encoder) info.encoder = ctx.encoder;

  // Duration: the edit list (if any) says what plays; the media header says what is stored.
  const ts = t.timescale;
  const st = t.samples;
  const mediaTicks = t.mediaDuration || (st ? st.t0[st.count - 1] + st.dur[st.count - 1] || 0 : 0);
  const edit = t.edits.find((x) => x.mediaTime >= 0);
  const mediaTime = edit ? edit.mediaTime : 0;
  let duration = ts ? (mediaTicks - mediaTime) / ts : undefined;
  if (edit && ctx.movieTimescale) {
    const editSecs = t.edits.reduce((a, x) => a + x.segDur, 0) / ctx.movieTimescale;
    if (editSecs > 0) duration = duration && duration > 0 ? Math.min(duration, editSecs) : editSecs;
  }
  if (duration !== undefined && duration > 0) info.duration = duration;
  if (info.duration && info.sampleRate) info.totalSamples = Math.round(info.duration * info.sampleRate);

  // Gapless info: iTunSMPB wins, then Opus pre-skip, then the edit list.
  const outPerTick = ts && info.sampleRate ? info.sampleRate / ts : 1;
  if (ctx.smpb) {
    // Apple writes HE-AAC gapless values at the core rate (media ticks); scale them to output samples.
    const { delay, padding, length } = ctx.smpb;
    const k = outPerTick !== 1 && Math.abs(delay + padding + length - mediaTicks) <= 1 ? outPerTick : 1;
    info.encoderDelay = Math.round(delay * k);
    info.encoderPadding = Math.round(padding * k);
    if (length) info.totalSamples = Math.round(length * k);
  } else if (e.preSkip !== undefined) {
    info.encoderDelay = e.preSkip;
  } else if (edit) {
    info.encoderDelay = Math.round(mediaTime * outPerTick);
    if (ctx.movieTimescale && ts) {
      const played = (t.edits.reduce((a, x) => a + x.segDur, 0) * ts) / ctx.movieTimescale;
      const pad = mediaTicks - mediaTime - played;
      // mvhd time is coarse (often ms), so ignore anything under one movie tick.
      info.encoderPadding = pad > ts / ctx.movieTimescale ? Math.round(pad * outPerTick) : 0;
    }
  }

  // Average bitrate: sample bytes over the time those samples cover (priming included).
  let bytes = 0;
  for (const f of frames) bytes += f.end - f.start;
  const covered = frames.length ? frames[frames.length - 1].time![1] - frames[0].time![0] : 0;
  if (bytes && covered > 0) info.bitrate = Math.round((bytes * 8) / covered);
  // Dolby streams are constant bitrate: the nominal rate from dac3/dec3 is exact.
  if ((!info.bitrate || e.codec === 'AC-3' || e.codec === 'E-AC-3') && e.avgBitrate) info.bitrate = e.avgBitrate;
  if (e.lossless) info.bitrateMode = e.codec === 'PCM' ? 'Uncompressed' : 'Lossless';
  else info.bitrateMode = bitrateMode(frames, e);
  return info;
}

/** CBR when the bytes per ~1 s window hardly change, else VBR. Frame sizes of CBR AAC still wobble (bit reservoir). */
function bitrateMode(frames: Segment[], e: EntryInfo): 'CBR' | 'VBR' {
  if (e.codec === 'AC-3') return 'CBR';
  if (frames.length < 3) return 'CBR';
  const first = frames[0].end - frames[0].start;
  if (frames.every((f) => f.end - f.start === first)) return 'CBR';
  const windows: number[] = [];
  let acc = 0, t0 = frames[0].time![0];
  for (const f of frames) {
    acc += f.end - f.start;
    if (f.time![1] - t0 >= 1) { windows.push(acc / (f.time![1] - t0)); acc = 0; t0 = f.time![1]; }
  }
  if (windows.length < 2) {
    // Short file: fall back to how much single frames vary.
    const sizes = frames.slice(1, -1).map((f) => f.end - f.start);
    const mean = sizes.reduce((a, x) => a + x, 0) / sizes.length;
    const sd = Math.sqrt(sizes.reduce((a, x) => a + (x - mean) ** 2, 0) / sizes.length);
    return sd / mean < 0.02 ? 'CBR' : 'VBR';
  }
  const mean = windows.reduce((a, x) => a + x, 0) / windows.length;
  const sd = Math.sqrt(windows.reduce((a, x) => a + (x - mean) ** 2, 0) / windows.length);
  return sd / mean < 0.03 ? 'CBR' : 'VBR';
}

function addNotes(ctx: Ctx, t: Track | undefined, e: EntryInfo | undefined, info: AudioInfo, frames: Segment[]): void {
  const notes = ctx.notes;
  const firstMdat = ctx.mdats.find((m) => m && m.children && (m.children as Segment[]).length > 1) ?? ctx.mdats[0];
  if (ctx.moovStart >= 0 && firstMdat && firstMdat.start !== undefined) {
    if (ctx.moovStart > firstMdat.start) {
      notes.push({ level: 'warn', text: 'The moov box (the index) comes after the audio data. A player that streams this file must fetch the end of the file before it can play. Tools can fix this with "fast start", which moves moov to the front.' });
    } else if (!ctx.moofCount) {
      notes.push({ level: 'info', text: 'This file is "fast start": the moov box (the index) comes before the audio data, so a player can start while the rest downloads.' });
    }
  }
  if (ctx.moofCount) {
    notes.push({ level: 'info', text: `This is a fragmented MP4 with ${plural(ctx.moofCount, 'fragment')}. Each moof box indexes only the samples in the mdat that follows it, so the file can be written or streamed piece by piece.` });
  }
  const others = ctx.tracks.filter((x) => x !== t);
  if (others.length) {
    notes.push({ level: 'info', text: `This file has ${plural(ctx.tracks.length, 'track')}. Only the first audio track is mapped to time. Samples of the other tracks are shown as other data.` });
  }
  if (t && e) {
    const edit = t.edits.find((x) => x.mediaTime >= 0);
    if (ctx.smpb) {
      notes.push({ level: 'info', text: `The iTunSMPB tag holds gapless info: ${n(ctx.smpb.delay)} priming samples at the start, ${n(ctx.smpb.padding)} padding samples at the end, and ${n(ctx.smpb.length)} real samples. Players cut the extra samples so albums play without gaps.` });
    }
    if (edit && edit.mediaTime > 0) {
      notes.push({ level: 'info', text: `The edit list skips the first ${n(edit.mediaTime)} ticks of media. These are priming samples: the encoder needs a warm-up before the real audio. Sample times here are media time, so they still include the priming.` });
    } else if (!ctx.smpb && e.codec.includes('AAC') && !edit) {
      notes.push({ level: 'info', text: 'There is no edit list or iTunSMPB tag, so players cannot know how many priming samples to skip. Expect a short silence at the start.' });
    }
    if (e.asc?.sbr || e.codec.startsWith('HE-AAC')) {
      const core = e.asc?.sampleRate ?? (info.sampleRate ?? 0) / 2;
      notes.push({ level: 'info', text: `HE-AAC runs its AAC core at ${fmtHz(core)}, half the output rate. SBR rebuilds the high frequencies, so each frame gives ${n((e.asc?.frameLength ?? 1024) * 2)} output samples.${e.codec === 'HE-AAC v2' ? ' Parametric Stereo also rebuilds stereo from a mono core.' : ''}` });
    }
    if (info.bitrateMode === 'VBR' && e.codec.includes('AAC')) {
      notes.push({ level: 'info', text: 'The bytes per second change over the file, so this AAC stream looks like variable bitrate (VBR). Every sample still decodes to the same number of audio samples.' });
    }
  }
  const picBytes = ctx.pictures.reduce((a, p) => a + (p.end - p.start), 0);
  if (picBytes && ctx.b.length) {
    const pct = (picBytes / ctx.b.length) * 100;
    if (pct >= 1) notes.push({ level: 'info', text: `Cover art takes ${pct.toFixed(1)}% of the file (${fmtBytes(picBytes)}).` });
  }
  if (frames.length && t?.samples && frames.length < t.samples.count) {
    notes.push({ level: 'warn', text: `The sample tables list ${n(t.samples.count)} samples, but only ${n(frames.length)} lie inside the file.` });
  }
}

// ---------------------------------------------------------------------------
// Explanations

const E = (title: string, body: string): Explanation => ({ title, body });

export const mp4Explanations: Record<string, Explanation> = {
  'mp4.boxheader': E('Box header', 'Every MP4 box starts with a 4-byte size and a 4-character type. A size of 1 means a 64-bit size follows; 0 means "to the end of the file". "Full" boxes add a 1-byte version and 3 bytes of flags.'),
  'mp4.box.unknown': E('Unknown box', 'A box type this parser does not know. MP4 readers skip boxes they do not understand, because the size field tells them where the next box starts. This is how the format stays extensible.'),
  'mp4.invalid': E('Unreadable bytes', 'These bytes do not form a valid box header. The file may be cut short, damaged, or have junk at the end.'),
  'mp4.wave.format': E('Format marker', 'A small QuickTime atom inside `wave` that repeats the codec type. It carries no other data.'),
  'mp4.terminator': E('Zero terminator', 'Some QuickTime writers end a list of boxes with zero bytes (or an empty box of type 0). Readers treat them as the end of the list.'),
  'mp4.box.ftyp': E('File type box (ftyp)', 'The first box in the file. It names a **major brand** (like `M4A `) and a list of **compatible brands**. A reader checks these to decide if it can play the file.'),
  'mp4.box.styp': E('Segment type box (styp)', 'Like ftyp, but at the start of a streaming segment (for example in DASH or HLS). It lists the brands this segment follows.'),
  'mp4.box.moov': E('Movie box (moov)', 'The index of the whole file. It holds one `trak` per track, with the codec setup and the sample tables that say where every sample is. Without moov, the audio in mdat cannot be found.'),
  'mp4.box.mvhd': E('Movie header (mvhd)', 'Global facts about the file: creation dates, a **timescale** (ticks per second) and the total duration in those ticks. Dates count seconds from 1 January 1904, the old Mac epoch.'),
  'mp4.box.iods': E('Object descriptor (iods)', 'An MPEG-4 systems box that lists profile levels. Most players ignore it, and many muxers no longer write it.'),
  'mp4.box.trak': E('Track (trak)', 'One stream: audio, video, subtitles or chapters. Each track has its own header, timescale, codec setup and sample tables.'),
  'mp4.box.tkhd': E('Track header (tkhd)', 'The track ID, its duration in the movie timescale, and flags that say if the track is enabled. Video tracks also give a width and height here.'),
  'mp4.box.tref': E('Track references (tref)', 'Links between tracks, such as a chapter track that belongs to an audio track.'),
  'mp4.box.edts': E('Edits (edts)', 'A wrapper for the edit list, which maps media time to presentation time.'),
  'mp4.box.elst': E('Edit list (elst)', 'Tells the player which part of the media to play. AAC encoders add **priming** samples at the start, and the edit list skips them with a media time of, say, 1,024. This is how MP4 plays without a gap or click at the start.'),
  'mp4.box.mdia': E('Media (mdia)', 'Groups everything about the track\'s media: its header, its handler (audio or video) and its sample information.'),
  'mp4.box.mdhd': E('Media header (mdhd)', 'The track\'s own clock. For audio the **timescale** is usually the sample rate, so one tick is one sample. The duration here counts every stored sample, priming included.'),
  'mp4.box.hdlr': E('Handler (hdlr)', 'Says what kind of data follows: `soun` for audio, `vide` for video. Inside a `meta` box, `mdir` means iTunes-style tags.'),
  'mp4.box.minf': E('Media information (minf)', 'Holds the media-type header (smhd for sound), the data references, and the sample table.'),
  'mp4.box.smhd': E('Sound media header (smhd)', 'Marks this track as audio. Its only real field is the stereo balance, which is almost always 0.'),
  'mp4.box.vmhd': E('Video media header (vmhd)', 'Marks this track as video. In audio files, a video track often holds cover art or a still image.'),
  'mp4.box.nmhd': E('Null media header (nmhd)', 'Used by tracks that are neither audio nor video, such as timed metadata.'),
  'mp4.box.dinf': E('Data information (dinf)', 'Says where the media data lives. In practice it always says "in this file".'),
  'mp4.box.dref': E('Data references (dref)', 'A list of data locations. MP4 once allowed samples in other files; today the single entry has a flag that means "this file".'),
  'mp4.box.url ': E('Data location (url)', 'One data reference. Flag 1 means the samples are in this same file, so no URL follows.'),
  'mp4.box.stbl': E('Sample table (stbl)', 'The heart of MP4. It splits the job of finding samples into small tables: **stts** (durations), **stsc** (samples per chunk), **stsz** (sizes) and **stco** (chunk positions). Each table uses runs, so a long file stays compact.'),
  'mp4.box.stsd': E('Sample descriptions (stsd)', 'Describes the codec: which format, how many channels, what sample rate, plus a codec-specific setup box (esds for AAC, alac for ALAC). A decoder needs this before it can read any sample.'),
  'mp4.box.stts': E('Time-to-sample (stts)', 'Gives the duration of every sample as runs of "N samples of D ticks". AAC frames are all 1,024 samples long, so one or two runs cover the whole file. Adding durations up gives each sample\'s start time.'),
  'mp4.box.ctts': E('Composition offsets (ctts)', 'Shifts display time away from decode time. Video with B-frames needs it; audio almost never does.'),
  'mp4.box.stsc': E('Sample-to-chunk (stsc)', 'Samples are stored in **chunks**: runs of back-to-back samples. This table says how many samples each chunk holds, as runs ("from chunk 5 on, 22 samples each"). It lets tracks be interleaved without listing every sample\'s position.'),
  'mp4.box.stsz': E('Sample sizes (stsz)', 'The size in bytes of every sample. With the chunk table, a player adds sizes to find where each sample starts. If all samples are the same size, one number replaces the whole list.'),
  'mp4.box.stz2': E('Compact sample sizes (stz2)', 'Like stsz, but each size uses only 4, 8 or 16 bits. It saves space when samples are small.'),
  'mp4.box.stco': E('Chunk offsets (stco)', 'The file position of each chunk, as 32-bit numbers. Samples inside a chunk sit back to back, so one offset per chunk is enough.'),
  'mp4.box.co64': E('Chunk offsets, 64-bit (co64)', 'Like stco, but with 64-bit positions for files larger than 4 GB.'),
  'mp4.box.stss': E('Sync samples (stss)', 'Lists the samples a decoder can start from. When it is missing, every sample is a sync sample, which is normal for audio.'),
  'mp4.box.sdtp': E('Sample dependencies (sdtp)', 'One byte per sample that says which samples depend on others. Mostly used for video.'),
  'mp4.box.sgpd': E('Sample group description (sgpd)', 'Describes properties shared by groups of samples. For AAC it is usually a **roll** group with distance -1: to decode a sample cleanly, decode the one before it first.'),
  'mp4.box.sbgp': E('Sample-to-group (sbgp)', 'Assigns runs of samples to the groups described in sgpd.'),
  'mp4.box.udta': E('User data (udta)', 'A place for data that is not needed to play the file, such as tags. iTunes puts its `meta` box here; QuickTime puts text atoms like `©nam` here directly.'),
  'mp4.box.meta': E('Metadata (meta)', 'Holds a handler box and the tag list. In ISO files it is a full box (with version and flags); QuickTime writes it without them, so readers must check.'),
  'mp4.box.ilst': E('iTunes tag list (ilst)', 'The list of tags. Each child box is named after its tag, such as `©nam` for the title, and holds a `data` box with the value.'),
  'mp4.box.free': E('Free space (free)', 'Empty space a player skips. Tools reserve it so they can grow the tags later without rewriting the whole file.'),
  'mp4.box.skip': E('Free space (skip)', 'Same as free: space that readers skip.'),
  'mp4.box.wide': E('Wide placeholder (wide)', 'An 8-byte QuickTime placeholder. If the next box grows past 4 GB, a writer can turn these bytes into a 64-bit size header in place.'),
  'mp4.box.mdat': E('Media data (mdat)', 'The raw encoded audio, with no headers or sync words between samples. Only the sample tables in moov (or moof) say where each sample starts and ends.'),
  'mp4.box.mvex': E('Movie extends (mvex)', 'Its presence says "this file is fragmented": more samples follow in moof boxes after the moov.'),
  'mp4.box.mehd': E('Movie extends header (mehd)', 'The total duration of all fragments, if the writer knew it in advance.'),
  'mp4.box.trex': E('Track extends defaults (trex)', 'Default duration, size and flags for samples in fragments, so each fragment can leave them out.'),
  'mp4.box.moof': E('Movie fragment (moof)', 'A small index for the mdat that follows it. Fragmented files repeat moof + mdat pairs, so a live stream can be written and played without a big index at the start.'),
  'mp4.box.mfhd': E('Fragment header (mfhd)', 'A sequence number that counts fragments in order.'),
  'mp4.box.traf': E('Track fragment (traf)', 'The part of a fragment that belongs to one track.'),
  'mp4.box.tfhd': E('Track fragment header (tfhd)', 'The track ID plus defaults (duration, size) for the samples in this fragment. Flags say which fields are present.'),
  'mp4.box.tfdt': E('Decode time (tfdt)', 'The decode time of the fragment\'s first sample, so a player can seek to a fragment without reading all the ones before it.'),
  'mp4.box.trun': E('Track run (trun)', 'The sample table of a fragment: a data offset and, per sample, a duration and size (unless defaults apply). It replaces stts, stsz and stco for fragmented files.'),
  'mp4.box.mfra': E('Fragment random access (mfra)', 'An optional index at the end of a fragmented file. It lists the time and position of fragments to speed up seeking.'),
  'mp4.box.tfra': E('Track fragment random access (tfra)', 'Time and file position of sync points in one track, for fast seeking in fragmented files.'),
  'mp4.box.mfro': E('Fragment random access offset (mfro)', 'The last box in the file. It gives the size of mfra, so a reader can find mfra by reading backwards from the end.'),
  'mp4.box.sidx': E('Segment index (sidx)', 'An index of media segments used by DASH streaming. It gives each segment\'s size and duration so a player can jump straight to one.'),
  'mp4.box.esds': E('Elementary stream descriptor (esds)', 'The AAC decoder setup, wrapped in MPEG-4 **descriptors**. Inside is the AudioSpecificConfig: a few bits that give the AAC profile, sample rate and channels.'),
  'mp4.box.btrt': E('Bit rate (btrt)', 'The buffer size, peak bitrate and average bitrate of the stream.'),
  'mp4.box.alac': E('ALAC magic cookie (alac)', 'The Apple Lossless setup: frame length, bit depth, channels, sample rate and tuning values for the Rice entropy coder.'),
  'mp4.box.dfLa': E('FLAC configuration (dfLa)', 'Holds FLAC metadata blocks, starting with STREAMINFO. It is the same data that starts a native .flac file.'),
  'mp4.box.dOps': E('Opus configuration (dOps)', 'The Opus setup: channel count, pre-skip (the encoder delay), original sample rate and output gain. It matches the OpusHead packet from Ogg, but in big-endian byte order.'),
  'mp4.box.dac3': E('AC-3 configuration (dac3)', 'Three bytes that describe the Dolby Digital stream: sample rate, speaker layout, whether there is an LFE (.1) channel, and bitrate.'),
  'mp4.box.dec3': E('E-AC-3 configuration (dec3)', 'Describes a Dolby Digital Plus stream: total bitrate and the layout of each substream.'),
  'mp4.box.wave': E('QuickTime codec extras (wave)', 'A QuickTime wrapper around the codec setup. Older .mov files put the esds box in here instead of directly in the sample entry.'),
  'mp4.box.frma': E('Original format (frma)', 'Names the real codec when the sample entry type is a wrapper.'),
  'mp4.box.chan': E('Channel layout (chan)', 'A Core Audio channel layout: which speaker each channel goes to.'),
  'mp4.entry.mp4a': E('AAC sample entry (mp4a)', 'Describes an MPEG-4 audio track, usually AAC. The channel count and rate here are only hints: the esds box inside has the real AudioSpecificConfig.'),
  'mp4.entry.alac': E('ALAC sample entry (alac)', 'Describes an Apple Lossless track. The magic cookie inside holds the settings the decoder needs.'),
  'mp4.entry.fLaC': E('FLAC sample entry (fLaC)', 'Describes a FLAC track inside MP4. The dfLa box inside holds the STREAMINFO block.'),
  'mp4.entry.Opus': E('Opus sample entry (Opus)', 'Describes an Opus track. The dOps box inside holds the pre-skip and channel setup.'),
  'mp4.entry.ac-3': E('AC-3 sample entry (ac-3)', 'Describes a Dolby Digital track. The dac3 box inside gives its layout and bitrate.'),
  'mp4.entry.ec-3': E('E-AC-3 sample entry (ec-3)', 'Describes a Dolby Digital Plus track. The dec3 box inside gives its substreams and bitrate.'),
  'mp4.entry.audio': E('Audio sample entry', 'Describes an audio track: format, channel count and sample rate. The four characters of its type name the codec.'),
  'mp4.entry.other': E('Sample entry', 'Describes a non-audio track, such as video or text. Its type names the codec.'),
  'mp4.desc.es': E('ES descriptor', 'The outer MPEG-4 descriptor. It carries a stream ID and flags, and wraps the decoder configuration.'),
  'mp4.desc.decoderconfig': E('Decoder config descriptor', 'Says which codec family the stream uses (0x40 = MPEG-4 Audio), plus the buffer size and bitrates.'),
  'mp4.desc.asc': E('AudioSpecificConfig', 'Two to five bytes that set up an AAC decoder: the **audio object type** (LC, HE-AAC…), the **sample rate index** and the **channel configuration**. HE-AAC adds bits that say SBR (and PS) are present.'),
  'mp4.desc.sl': E('SL config descriptor', 'A leftover from MPEG-4 streaming. In MP4 files it always holds the value 2.'),
  'mp4.desc.other': E('MPEG-4 descriptor', 'A descriptor this parser does not decode. Each descriptor starts with a 1-byte tag and a variable-length size.'),
  'mp4.ilst.item': E('iTunes tag', 'One tag, such as the title or artist. The box type is the tag name (`©nam`, `©ART`, `trkn`…). The value is in the `data` box inside.'),
  'mp4.ilst.freeform': E('Freeform tag (----)', 'A custom tag with a namespace (`mean`) and a name (`name`). iTunes uses it for things like `iTunSMPB`, which stores the gapless playback info.'),
  'mp4.ilst.cover': E('Cover art (covr)', 'Embedded cover art. It can hold one or more `data` boxes, each with a JPEG or PNG image.'),
  'mp4.ilst.data': E('Tag value (data)', 'Holds the tag value. A type code says how to read it: 1 is UTF-8 text, 21 is an integer, 0 is binary (used for track and disc numbers).'),
  'mp4.ilst.image': E('Image data', 'A `data` box holding a picture. Type 13 means JPEG and 14 means PNG. The image bytes start after 8 bytes of type and locale.'),
  'mp4.ilst.mean': E('Tag namespace (mean)', 'The owner of a freeform tag, as a reverse domain name like `com.apple.iTunes`.'),
  'mp4.ilst.name': E('Tag name (name)', 'The name of a freeform tag, such as `iTunSMPB` or `iTunNORM`.'),
  'mp4.udta.text': E('QuickTime text tag', 'A QuickTime-style tag directly in udta: a 2-byte length, a 2-byte language code and the text.'),
  'mp4.sample': E('Audio sample', 'One compressed packet of audio. For AAC, one sample is one frame of 1,024 audio samples (2,048 for HE-AAC). MP4 stores samples with no headers; the sample tables give each one\'s position, size and time.'),
  'mp4.sample.other': E('Sample of another track', 'A sample that belongs to a different track, such as a video frame or a cover image. Tracks share the mdat and are interleaved in chunks.'),
};

const TOP_TYPES = new Set(['ftyp', 'styp', 'moov', 'moof', 'mdat', 'free', 'skip', 'wide', 'pnot', 'uuid', 'sidx', 'PICT']);

export const mp4Parser: FormatParser = {
  id: 'mp4',
  detect(b: Uint8Array): boolean {
    if (b.length < 8) return false;
    const t = fourcc(b, 4);
    if (t === 'ftyp' || t === 'styp' || t === 'moov' || t === 'moof') return true;
    if (!TOP_TYPES.has(t)) return false;
    // Something like 'free' or 'mdat' first: check that a sane box follows.
    const size = u32be(b, 0);
    if (size < 8 || size + 8 > b.length) return t === 'mdat' && size >= 8;
    return TOP_TYPES.has(fourcc(b, size + 4));
  },
  parse(b: Uint8Array): ParsedFile {
    try {
      return parse(b);
    } catch (e) {
      // Never throw on damaged input: report what went wrong instead.
      return {
        format: 'mp4', container: 'MPEG-4', size: b.length,
        segments: b.length ? [{ kind: 'unknown', type: 'mp4.invalid', name: 'Unreadable file', start: 0, end: b.length, warning: String((e as Error).message) }] : [],
        frames: [], audio: { codec: 'Unknown', lossless: false }, tags: [], pictures: [],
        notes: [{ level: 'warn', text: `The parser failed: ${(e as Error).message}` }],
      };
    }
  },
  explanations: mp4Explanations,
};
